import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Eye,
  EyeOff,
  GripVertical,
  ListOrdered,
  Paperclip,
  RotateCcw,
  Settings2,
  Info,
  Loader2,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { NumberInput } from '@/components/molecules/NumberInput';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { PRICEBOOK_UNIT_OPTION_GROUPS } from '@/lib/constants/pricebookUnits';
import { Textarea } from '@/components/atoms/textarea';
import {
  allowsDynamicPricing,
  getDynamicPricingIncompleteReason,
  getDynamicPricingLabel,
  isTMOverrideEligibleType,
  pricesFromHours,
} from '@/lib/sales/pricingMode';
import { LineItemPricingControls } from '@/components/molecules/LineItemPricingControls';
import { PercentSliderInput } from '@/components/molecules/PercentSliderInput';
import { ProfitUpliftEditor } from '@/components/organisms/pricebook/ProfitUpliftEditor';
import {
  billsAtLineRate,
  configuredServiceParts,
  dynamicServiceLabor,
  dynamicServicePrice,
  documentServiceParts,
  linkedPartLine,
  materializedPartAmount,
  materializedServiceParts,
  materializedUnitAmount,
} from '@/lib/sales/dynamic-service-price';
import { laborRateLabel } from '@/lib/sales/service-labor';
import { rollUpService } from '@/lib/sales/service-rollup';
import {
  PriceStrategyToggle,
  type PriceStrategy,
} from '@/components/molecules/PriceStrategyToggle';
import { TMStatusBadge } from '@/components/molecules/TMStatusBadge';
import { Badge } from '@/components/atoms/badge';
import { FormAccordionCard, FormSectionHeader } from '@/components/molecules';
import { apiGet, apiPost, apiDelete, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import {
  isMarkupAvailable,
  markupFromCostPrice,
  unitPriceFromCostMarkup,
} from '@/lib/sales/line-item-pricing';
import type { MembershipPlanSummary } from '@/lib/membership/format-plan-summary';
import type { LaborRateProfile, ServiceLaborLine, Supplier } from '@/types/pricebook';
import type {
  DisplayConfig,
  DynamicServicePricingConfig,
  MarkupProfile,
  MaterializedServicePart,
} from '@/types/pricebook';
import { DurationMinutesInput } from '@/components/molecules/DurationInput';
import { useLaborRateProfiles, useMarkupProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { EquipmentLineAssociation } from '@/components/organisms/equipment/EquipmentLineAssociation';
import type { Equipment } from '@/types/equipment';
import { JobEquipmentInstallationFields } from '@/components/organisms/jobs/JobEquipmentInstallationFields';
import type { JobEquipmentInstallationUnitDraft } from '@/components/organisms/jobs/support/job-equipment-installations';
import { renderGroupedLines } from '@/components/organisms/sales/ServiceBundleGroup';
import {
  basePriceOf,
  chargesRevenue,
  internalPriceDisplay,
  isManualPriceOverride,
  unitCostOf,
  unitPriceOf,
  type PricingTreatment,
} from '@/lib/sales/line-economics';
import { groupedLineOrder, groupedReorder, soldLineSet } from '@/lib/sales/service-components';
import { formatQuantityWithUnit, unitLabel } from '@/lib/sales/quantity-display';
import {
  estimateSectionForType,
  LINE_SECTIONS,
  sectionForType,
  type LineSection,
} from '@/lib/sales/line-sections';
import {
  LineTaxAccountingFields,
  type LineTaxAccounting,
} from '@/components/molecules/LineTaxAccountingFields';
import {
  LineMemberPricingFields,
  type LineMemberPricing,
} from '@/components/molecules/LineMemberPricingFields';
import { SaveLineToPricebookButton } from '@/components/molecules/SaveLineToPricebookButton';
import { ServiceLaborCostEditor } from '@/components/organisms/sales/ServiceLaborCostEditor';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EstimateLineItem {
  _rowId: string;
  /** Service-call visit-price credit: false = always charged (e.g. materials);
   * true/undefined = credits toward work when the customer proceeds. */
  isServiceCallFee?: boolean;
  /**
   * The CATALOG item this line came from. Read here to match a document line to
   * the part the service's configuration snapshot names, so a configured
   * quantity can carry to the line that will actually be installed.
   */
  lineItemId?: string;
  /** NOT-590 — stable per-line key linking a service to its component lines. */
  lineKey?: string;
  parentLineKey?: string;
  membershipCustomerChoosesBillingOption?: boolean;
  componentDisplayMode?: 'ITEMIZED' | 'ROLLED_UP';
  name: string;
  code?: string;
  description?: string;
  type: string;
  quantity: number;
  /** Unit of measure; defaults from the pricebook but is overridable per line
   *  via the qty-row selector (NOT-525). */
  unit?: string | null;
  basePrice: number;
  /**
   * The price the operator set for THIS document — the spec's `unitPrice`, in
   * the field that is actually persisted and that the operations pricing engine
   * resolves first. `basePrice` stays the pricebook's original, so a line can
   * always be put back to what the catalog says. `null`/absent means no
   * override, which is NOT the same as an override of 0.
   */
  manualPriceOverride?: number | null;
  laborRate?: number;
  costBasis?: number;
  costSource?: 'DIRECT' | 'SUPPLIER';
  costSupplierId?: string;
  costSupplierName?: string;
  markupPercent?: number;
  /**
   * MATERIAL / EQUIPMENT: billed at its own price and printed for the customer.
   * `false` bills nothing and hides the row; the cost still counts. Undefined is
   * chargeable, so lines saved before the flag existed bill unchanged.
   */
  chargeable?: boolean;
  /**
   * How THIS document charges for the part — the relationship's answer, which
   * `chargeable` above only defaults. Read both through `chargesRevenue` rather
   * than testing either directly.
   */
  pricingTreatment?: PricingTreatment | null;
  /**
   * EQUIPMENT: kept off the customer's document. Default false — equipment
   * prints even when nothing is charged for it, because what is being installed
   * is the substance of the offer. It suppresses the customer view ONLY: the
   * line keeps its cost and its contribution to the service that carries it.
   */
  hiddenFromCustomer?: boolean;
  priceStrategy?: string;
  /**
   * Hours of work on this service. DYNAMIC prices FROM them; FLAT records them
   * as COST and its typed price is untouched.
   */
  estimatedHours?: number;
  /** What an hour of this service COSTS us — never what it sells for. */
  laborCostRate?: number;
  /** The labor rows the hours are made of. Cost only. */
  laborCostLines?: ServiceLaborLine[];
  taxable?: boolean;
  pricebookTaxable?: boolean;
  /** How the line is classified for tax, and where its money posts. */
  taxLineType?: string | null;
  taxCategory?: string | null;
  taxCode?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  isOptional: boolean;
  isSelected?: boolean;
  isUpsell?: boolean;
  upsellContext?: string | null;
  isCustom?: boolean;
  membershipPlanSummary?: MembershipPlanSummary;
  /** NOT-605 — item-level member pricing, so the badge labels a fixed member
   *  price vs. the plan discount, and the preview matches the saved estimate. */
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  displayConfig?: DisplayConfig | null;
  equipmentIds?: string[];
  equipmentLocationOverrideReason?: string;
  dynamicPricingConfig?: DynamicServicePricingConfig;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  provenance?: {
    source: 'ESTIMATE' | 'JOB_TYPE';
    label: string;
  };
}

interface MembershipDiscountInfo {
  planName: string | null;
  discountType: string | null;
  discountValue: number;
  waivers?: Array<{ lineItemId?: string | null }>;
  plans?: MembershipDiscountInfo[];
}

interface LineItemPreview {
  lineSubtotal: number;
  lineTax: number;
  lineTotal: number;
  membershipDiscountAmount?: number;
  /** Amount the customer pays for this line after membership and document discounts. */
  lineAfterDiscount?: number;
  documentDiscountAmount?: number;
}

interface LineItemAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  isInternal: boolean;
  downloadUrl?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function lineIsWaivedByMembership(
  item: EstimateLineItem,
  membership: MembershipDiscountInfo,
): boolean {
  const plans = membership.plans?.length ? membership.plans : [membership];
  return plans.some((plan) =>
    (plan.waivers ?? []).some((waiver) =>
      waiver.lineItemId
        ? waiver.lineItemId === item.lineItemId
        : String(item.type ?? '')
            .trim()
            .toUpperCase() === 'FEE',
    ),
  );
}

const INPUT_CLS =
  '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

function parseDraftNumber(value: string, fallback = 0): number {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeLineItemType(type: string | null | undefined): string {
  return String(type ?? '')
    .trim()
    .toUpperCase();
}

function isMembershipLineType(type: string | null | undefined): boolean {
  return normalizeLineItemType(type) === 'MEMBERSHIP';
}

/**
 * A field label, not a section stamp.
 *
 * UPPERCASE + tracking is the heading style this file used for every field in
 * the expanded editor — "UNIT COST", "DESCRIPTION", "TAX" — which reads as a
 * stack of headings rather than as labelled inputs, and is a second label
 * language on any form whose other labels are sentence case. Same weight,
 * same size, said normally.
 */
function EditorFieldLabel({ children }: { children: ReactNode }) {
  return <label className="block text-xs font-semibold text-foreground">{children}</label>;
}

// ─── Line Item Attachment Panel ───────────────────────────────────────────────

interface PendingFile {
  name: string;
  file: File;
}

/**
 * What a line's amount actually comes to, after what has been taken off it.
 *
 * `lineSubtotal` is the list figure. A membership discount and a document
 * discount both come off it before the customer pays, and a surface showing
 * the subtotal beside an editor showing the discounted amount is two answers
 * to "what does this line cost".
 */
export function lineEffectiveSubtotal(
  preview:
    | {
        lineSubtotal: number;
        lineAfterDiscount?: number;
        membershipDiscountAmount?: number;
        documentDiscountAmount?: number;
      }
    | undefined,
  fallback: number,
): number {
  if (!preview) return fallback;
  return (
    preview.lineAfterDiscount ??
    Math.max(
      0,
      preview.lineSubtotal -
        (preview.membershipDiscountAmount ?? 0) -
        (preview.documentDiscountAmount ?? 0),
    )
  );
}

/** A line the operator typed, which therefore has no catalog original. */
export function lineIsHandTyped(line: { isCustom?: boolean; code?: string }): boolean {
  return line.isCustom ?? !line.code;
}

/**
 * Where a typed unit price is written, and what clearing the field means.
 *
 * Both answers depend on whether the line has a catalog original behind it,
 * and getting either wrong is silent: writing a hand-typed line's price to the
 * override leaves `basePrice` frozen, and `memberPrice / basePrice` stops
 * reducing to the fixed member price that was configured for it (see
 * `writeUnitPrice`). Clearing means "go back to the catalog", which only
 * exists where there is a catalog price to go back to.
 */
export function lineUnitPriceWriter(opts: {
  line: { isCustom?: boolean; code?: string; basePrice: number };
  index: number;
  updateManualPriceOverride?: (index: number, price: number | null) => void;
  updateBasePrice?: (index: number, price: number) => void;
}) {
  const handTyped = lineIsHandTyped(opts.line);
  return {
    write(price: number) {
      if (opts.updateManualPriceOverride && !handTyped) {
        opts.updateManualPriceOverride(opts.index, price);
        return;
      }
      opts.updateBasePrice?.(opts.index, price);
    },
    /** True when clearing means something — i.e. there is an override to drop. */
    canClear: !handTyped && Boolean(opts.updateManualPriceOverride),
    clear() {
      if (!handTyped) opts.updateManualPriceOverride?.(opts.index, null);
    },
  };
}

/**
 * What a line's quantity is allowed to be.
 *
 * A part folded into a service may go to zero — that is how it is taken out
 * without being deleted. A line of its own may not: quantity 0 on a required
 * service is a line that bills nothing and still sits in the sale.
 */
export function lineQuantityMinimum(line: { parentLineKey?: string }): number {
  return line.parentLineKey ? 0 : 1;
}

/**
 * A membership line's quantity and price are the plan's, not the operator's:
 * one plan per option, priced by the billing option chosen for it.
 */
export function lineMembershipLocked(line: { type: string }): boolean {
  return isMembershipLineType(line.type);
}

/** Why those two fields refuse to be edited, for the field that refuses. */
export function lineMembershipLockReason(line: {
  type: string;
  quantity: number;
}): string | undefined {
  if (!lineMembershipLocked(line)) return undefined;
  return line.quantity !== 1
    ? `This legacy document stores quantity ${line.quantity}. Remove and re-add the membership to repair it to quantity 1.`
    : 'A membership plan can only be added once per estimate option.';
}

function LineItemAttachmentPanel({
  estimateId,
  lineItemId,
  onPendingFilesChange,
}: {
  estimateId: string | undefined;
  lineItemId: string;
  onPendingFilesChange?: (rowId: string, files: File[]) => void;
}) {
  const [attachments, setAttachments] = useState<LineItemAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function load() {
    if (!estimateId || loaded) return;
    try {
      const data = await apiGet<LineItemAttachment[]>(
        operationsClient,
        `${opsEstimateEndpoints.attachments(estimateId)}?lineItemId=${encodeURIComponent(lineItemId)}`,
      );
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
    }
    setLoaded(true);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    if (inputRef.current) inputRef.current.value = '';

    // Pre-persist (no estimateId yet): queue all selected files.
    if (!estimateId) {
      const next = [...pendingFiles, ...files.map((file) => ({ name: file.name, file }))];
      setPendingFiles(next);
      onPendingFilesChange?.(
        lineItemId,
        next.map((p) => p.file),
      );
      return;
    }

    setUploading(true);
    try {
      const newAttachments: LineItemAttachment[] = [];
      for (const file of files) {
        try {
          const { uploadUrl, attachment } = await apiPost<{
            uploadUrl: string;
            attachment: LineItemAttachment;
          }>(operationsClient, opsEstimateEndpoints.attachmentUploadUrl(estimateId), {
            filename: file.name,
            contentType: file.type,
            fileSize: file.size,
            lineItemId,
          });
          await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          newAttachments.push(attachment);
        } catch {
          // silent per-file — user can retry the ones that failed
        }
      }
      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    } finally {
      setUploading(false);
    }
  }

  function removePendingFile(name: string) {
    const next = pendingFiles.filter((p) => p.name !== name);
    setPendingFiles(next);
    onPendingFilesChange?.(
      lineItemId,
      next.map((p) => p.file),
    );
  }

  async function handleDelete(attachmentId: string) {
    if (!estimateId) return;
    await apiDelete(
      operationsClient,
      `${opsEstimateEndpoints.attachments(estimateId)}/${attachmentId}`,
    );
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  }

  return (
    <div className="space-y-2" onMouseEnter={load}>
      {loaded && attachments.length > 0 && (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{a.fileName}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => handleDelete(a.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {pendingFiles.length > 0 && (
        <ul className="space-y-1">
          {pendingFiles.map((p) => (
            <li
              key={p.name}
              className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-warning" />
              <span className="min-w-0 flex-1 truncate">{p.name}</span>
              <span className="shrink-0 text-[10px] text-warning">queued</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => removePendingFile(p.name)}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,application/pdf,video/mp4,video/quicktime"
          onChange={handleFileChange}
          disabled={uploading}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
          disabled={uploading}
          onClick={() => {
            load();
            inputRef.current?.click();
          }}
        >
          <Paperclip className="h-3 w-3" />
          {uploading ? 'Uploading…' : 'Attach file'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Editable unit-of-measure selector for a line item — reuses the pricebook's
 * grouped UOM options so a job/estimate line can override the catalog default
 * (NOT-525). The trigger shows the short code; the menu shows full labels. A
 */
function UnitSelect({ value, onChange }: { value: string; onChange: (unit: string) => void }) {
  return (
    <Select value={value} onValueChange={(next) => next && onChange(next)}>
      <SelectTrigger
        aria-label="Unit of measure"
        className="h-8 w-[4.25rem] shrink-0 bg-background px-2 text-xs text-muted-foreground"
      >
        <span className="truncate">{value || 'Unit'}</span>
      </SelectTrigger>
      {/* position="popper" anchors the menu to the trigger and constrains it to
          the viewport (scrolling inside). The default "item-aligned" mode aligns
          the selected option with the trigger, which pushed this long UOM list
          off the bottom of the screen when a lower option was selected. */}
      <SelectContent position="popper" className="max-h-72">
        {PRICEBOOK_UNIT_OPTION_GROUPS.map(({ category, options }) => (
          <SelectGroup key={category}>
            <SelectLabel>{category}</SelectLabel>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Mobile card ─────────────────────────────────────────────────────────────

function DynamicServiceCustomizer({
  item,
  lineItems,
  index,
  updateConfig,
  updateMarkupProfile,
  updateQuantity,
  partPicker,
  removeConfiguredPart,
}: {
  item: EstimateLineItem & { dynamicPricingConfig: DynamicServicePricingConfig };
  index: number;
  lineItems: EstimateLineItem[];
  updateConfig: (index: number, config: DynamicServicePricingConfig) => void;
  updateMarkupProfile?: (
    index: number,
    target: 'MATERIAL' | 'EQUIPMENT',
    profile: MarkupProfile,
  ) => void;
  updateQuantity: (index: number, quantity: number) => void;
  /**
   * The search that adds a material or equipment this service was NOT configured
   * with. Omitted by surfaces that cannot save the configuration, which hides it
   * rather than offering a control whose result would be discarded.
   */
  partPicker?: ReactNode;
  /** Take a configured part back out, by its position in the configuration. */
  removeConfiguredPart?: (partIndex: number) => void;
}) {
  const laborProfiles = useLaborRateProfiles();
  const markupProfiles = useMarkupProfiles();
  const config = item.dynamicPricingConfig;
  const [step, setStep] = useState(0);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [upliftOpen, setUpliftOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const steps = ['Labor', 'Parts', 'Adjustments', 'Review'];
  const parts = lineItems
    .map((line, lineIndex) => ({ line, lineIndex }))
    .filter(({ line }) => line.parentLineKey === item.lineKey);

  // WHAT THE SERVICE IS CONFIGURED TO CONTAIN — its own snapshot, taken when
  // the catalog priced it, and the only thing this whole editor reads.
  //
  // Never the document's part lines. Those are the operator's: repricing the
  // filter to $1,000 in the Materials section is a fact about that line, and it
  // says nothing about what the service was configured with. Reading them here
  // is the leak the owner reported — the wizard showed "$100.00 per each" for a
  // filter the catalog priced at $16. Owner's rule: "anything that happens
  // outside configuration should not affect configuration and price."
  //
  // A service materialized before the snapshot existed, or built by hand, has
  // none, and falls back to its lines — the only account of its parts there has
  // ever been.
  //
  // Each configured part is paired with the document ROW it was expanded onto,
  // where there is one and there is no doubt which — `linkedPartLine`, the one
  // answer to that question, shared with the state hook so the row a quantity
  // carries to and the row a removal takes are never different rows. The
  // pairing decides only what the part is DOING on the document — declined, or
  // also billed on its own row. It never supplies the amount: that comes from
  // the snapshot, always.
  //
  // The list itself comes from `configuredServiceParts` — the ONE derivation of
  // it, shared for the same reason. A part is addressed by its POSITION in this
  // list, so a second implementation of the fallback drifting by one line would
  // remove a part in place of another.
  //
  // ONE MEANING, whichever source answered. A snapshot entry states a quantity
  // PER delivery of the service, and the fallback now converts the document's
  // absolute rows to the same footing, so nothing downstream has to ask where
  // the number came from before it can read it. It used to: the two meanings
  // both arrived through this one list, and a legacy service's absolute count
  // was edited as though it were per-delivery.
  const snapshotParts = materializedServiceParts(config);
  const configuredParts: MaterializedServicePart[] = configuredServiceParts(
    config,
    parts.map(({ line }) => line),
    item.quantity,
  );
  const linkedEntry = (part: MaterializedServicePart) => {
    const line = linkedPartLine(
      parts.map(({ line: partLine }) => partLine),
      part,
    );
    return line ? parts.find((entry) => entry.line === line) : undefined;
  };
  // WHICH rate a line uses, named — for the collapsed scope card and the card
  // the operator edits, which named it two different ways. One helper, shared
  // with the pricebook's own setup dialog, so the wording cannot drift again.
  const rateLabel = (labor: ServiceLaborLine): string =>
    laborRateLabel(labor, laborProfiles.data ?? []);

  const configuredEntries: { part: MaterializedServicePart; line?: EstimateLineItem }[] =
    configuredParts.map((part, partIndex) => ({
      part,
      // WITHOUT a snapshot the entry was derived from the line at this same
      // position, so that line is the answer and nothing has to be matched.
      line: config.materializedParts ? linkedEntry(part)?.line : parts[partIndex]?.line,
    }));

  /**
   * Changing what the service CONTAINS — a configuration change, so it reprices.
   *
   * The opposite direction from the Materials section, and the two must not be
   * confused: this writes the configuration and the price follows; editing the
   * part's own line writes that line and nothing else moves.
   *
   * The document line follows the configured quantity where one is linked, so
   * job costing counts the parts the service is actually configured to consume.
   * One way only — the line never writes back.
   *
   * MULTIPLIED THROUGH THE SERVICE'S OWN QUANTITY on the way across — but ONLY
   * when the snapshot is what produced this list, because only then is the
   * number per-service. The configuration says what ONE delivery of the service
   * consumes; the line says how many are consumed in total — the same absolute
   * figure `expandComponentLines` writes as `component.quantity * line.quantity`
   * in operations. Copying the configured number straight across halved the line
   * on a service sold twice, and with it the job's cost and its margin
   * (NOT-633).
   *
   * THE LEGACY PATH MEANS THE SAME THING NOW. A service materialized before the
   * snapshot existed has none, and `configuredServiceParts` falls back to the
   * document's part rows — where the quantity is absolute. It converts them,
   * dividing by the service's quantity, so this multiplication is the exact
   * inverse and holds on both paths. Before that conversion the fallback handed
   * the absolute figure straight to a per-service field: the steppers read
   * `part.quantity ± 1` off the same row, so a service at quantity 2 with a part
   * line at 4 turned one click of `+` into 5 x 2 = 10, and compounded after it.
   *
   * The carry happens only when the target is UNAMBIGUOUS — `linkedPartLine`
   * again, so it lands on the row this panel is already describing. The
   * pricebook cannot hold the same item twice under one service, so the
   * snapshot's ids are always distinct; the DOCUMENT's are not, because a part
   * can also be attached by hand and the rows can be reordered. There is then no
   * fact about which is "the" configured line, and rewriting an arbitrary one is
   * a guess. Nothing is carried instead.
   *
   * The service's price does not depend on any of this. It is calculated from
   * the snapshot written just above, never from these lines, so an uncarried
   * quantity costs a line its costing figure — not the estimate its price.
   */
  const setConfiguredQuantity = (partIndex: number, quantity: number) => {
    const next = Math.max(0, quantity);
    const part = configuredParts[partIndex];
    if (!part) return;
    updateConfig(index, {
      ...config,
      materializedParts: configuredParts.map((candidate, candidateIndex) =>
        candidateIndex === partIndex
          ? { ...candidate, unitAmount: materializedUnitAmount(candidate), quantity: next }
          : { ...candidate, unitAmount: materializedUnitAmount(candidate) },
      ),
    });
    const linked = linkedEntry(part);
    // A service at quantity 0 still describes one delivery of itself, so the
    // floor of 1 keeps a configuration edit from silently zeroing the row — the
    // same floor the conversion divides by, so the two round-trip.
    if (linked) updateQuantity(linked.lineIndex, next * Math.max(1, item.quantity));
  };

  // What the uplift adds, and what it is charged on — from the SAME roll-up
  // that prices the service, over the SAME parts the price is calculated from,
  // so the figure the operator reads here and the money the customer is charged
  // cannot drift apart.
  const pricedParts = documentServiceParts(
    config,
    parts.map(({ line }) => line),
  );
  const upliftRollup = rollUpService({
    labor: dynamicServiceLabor(config),
    parts: pricedParts,
    surcharge: config.surcharge,
  });
  // The review step's breakdown, from the one function that PRICES a dynamic
  // service — the same one the state hook reprices `basePrice` with, so the
  // panel cannot state a breakdown the saved line disagrees with.
  //
  // Over the SNAPSHOT, like `upliftRollup` above it. Reading the document's part
  // lines here is the leak this branch closes: a filter the operator repriced to
  // $1,000 in the Materials section would otherwise turn up inside the service's
  // own breakdown, describing a configuration it does not have.
  const pricing = dynamicServicePrice({
    config,
    parts: pricedParts,
  });
  // Only parts carried by the service line belong in its breakdown. A part
  // billed separately still contributes to the offer total, but its own row
  // owns that money; counting it here as well would describe a double charge.
  const includedPartsTotal = pricing.partsContribution - pricing.partsSeparatelyCharged;
  // The service sells for its stored price: the figure the row, the subtotal,
  // the tax and the invoice all state. Labor and adjustments is what is left of
  // it once the parts inside it are accounted for, so the rows add up to the
  // total rather than to a third number nothing else charges.
  const serviceTotal = item.basePrice;
  const laborAndAdjustments = serviceTotal - includedPartsTotal;
  // What each configured part is DOING, answered by the same rules that price
  // it — over the SNAPSHOT, like everything else this editor states.
  //
  // A declined optional part is offered rather than sold and contributes
  // nothing (`partAmounts` returns zero for it), so it is counted nowhere.
  // Separately billed parts are named separately rather than also counted as
  // included, matching which line actually carries their amount.
  const partRoles = configuredEntries.map(({ part, line }) => ({
    part,
    line,
    amount: materializedPartAmount(part),
    declined: line?.isSelected === false,
    billedSeparately: line ? chargesRevenue(line) : false,
  }));
  const includedCount = partRoles.filter((role) => !role.declined && !role.billedSeparately).length;
  const separatelyBilledCount = partRoles.filter(
    (role) => !role.declined && role.billedSeparately,
  ).length;

  if (!editing) {
    const minutes = (config.laborLines ?? []).reduce(
      (sum, labor) => sum + labor.durationMinutes,
      0,
    );
    return (
      <div className="space-y-3 rounded-lg border bg-background p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Service scope</p>
            <p className="text-xs text-muted-foreground">
              {Math.floor(minutes / 60)}h {minutes % 60}m labor · {includedCount} included part
              {includedCount === 1 ? '' : 's'}
              {separatelyBilledCount > 0 ? ` · ${separatelyBilledCount} billed separately` : ''}
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
            Edit configuration
          </Button>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {(config.laborLines ?? []).map((labor, laborIndex) => (
            <div key={labor.id ?? laborIndex} className="rounded-md bg-muted/50 px-3 py-2 text-xs">
              <p className="font-medium">{labor.label || `Labor ${laborIndex + 1}`}</p>
              <p className="text-muted-foreground">
                {Math.floor(labor.durationMinutes / 60)}h {labor.durationMinutes % 60}m ·{' '}
                {rateLabel(labor)}
              </p>
            </div>
          ))}
        </div>
        {configuredParts.length > 0 && (
          <div className="grid gap-2 md:grid-cols-2">
            {partRoles.map(({ part, line, amount, declined, billedSeparately }, partIndex) => (
              <div
                key={`${part.name}-${partIndex}`}
                className="flex items-center gap-2 rounded-md border px-2 py-2"
              >
                <div className="size-9 shrink-0 rounded border bg-muted" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">{part.name}</p>
                  {/* An amount here is the SNAPSHOT's — what the part is worth
                      to this service — and it is stated only where that is the
                      whole story. A part billed on its own row is priced THERE,
                      and that row carries the operator's own price override,
                      which this card does not see; a figure printed beside it
                      would sooner or later read as what the customer is charged
                      and contradict the row one click away. Naming the row that
                      owns the money says more than a number that can drift, and
                      a declined part is billed nowhere at all. */}
                  <p className="text-xs text-muted-foreground">
                    {/* Prose on a card, with no Qty heading above it. */}
                    {formatQuantityWithUnit(part.quantity, line?.unit, 'word')} ·{' '}
                    {declined
                      ? 'not selected'
                      : billedSeparately
                        ? 'billed on its own row'
                        : formatCurrency(amount)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        {(config.complexityAdjustmentPercent ?? 0) > 0 || config.materializedAfterHours ? (
          <p className="text-xs text-muted-foreground">
            Adjustments:{' '}
            {(config.complexityAdjustmentPercent ?? 0) > 0
              ? `${config.complexityAdjustmentPercent}% complexity`
              : ''}
            {(config.complexityAdjustmentPercent ?? 0) > 0 && config.materializedAfterHours
              ? ' · '
              : ''}
            {config.materializedAfterHours ? 'After-hours' : ''}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border bg-background p-4">
      <div>
        <p className="text-sm font-semibold">Configure service</p>
        <p className="text-xs text-muted-foreground">
          Set the work required for this document. The price updates as you go.
        </p>
      </div>
      <div className="grid grid-cols-4 gap-2" aria-label="Service configuration steps">
        {steps.map((label, stepIndex) => (
          <button
            key={label}
            type="button"
            onClick={() => setStep(stepIndex)}
            className={cn(
              'rounded-md px-2 py-2 text-xs font-medium transition-colors',
              step === stepIndex
                ? 'bg-primary text-primary-foreground'
                : stepIndex < step
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground',
            )}
          >
            <span className="mr-1.5">{stepIndex + 1}</span>
            {label}
          </button>
        ))}
      </div>

      {step === 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {(config.laborLines ?? []).map((labor, laborIndex) => (
            <div key={labor.id ?? laborIndex} className="rounded-md border p-2.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium">
                  {labor.label || `Labor ${laborIndex + 1}`}
                </span>
                <span className="rounded-full bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                  {rateLabel(labor)}
                </span>
              </div>
              <DurationMinutesInput
                value={labor.durationMinutes}
                onChange={(durationMinutes) =>
                  updateConfig(index, {
                    ...config,
                    laborLines: config.laborLines!.map((line, i) =>
                      i === laborIndex ? { ...line, durationMinutes } : line,
                    ),
                  })
                }
              />
              {/* A custom rate is a number the operator chose, so it is theirs
                  to change here. A profile-backed line offers the profiles
                  instead — the two are the same question ("what does this hour
                  charge") answered from different places, and the line said
                  neither. */}
              {labor.rateSource === 'FIXED' ? (
                <label className="mt-2 block space-y-1">
                  <span className="text-[11px] text-muted-foreground">Rate / hour</span>
                  <NumberInput
                    aria-label={`${labor.label || `Labor ${laborIndex + 1}`} hourly rate`}
                    min={0}
                    step="0.01"
                    className="h-8 text-xs"
                    value={labor.fixedRate ?? 0}
                    emptyValue={0}
                    onChange={(next) =>
                      updateConfig(index, {
                        ...config,
                        laborLines: config.laborLines!.map((line, i) =>
                          i === laborIndex ? { ...line, fixedRate: Math.max(0, next ?? 0) } : line,
                        ),
                      })
                    }
                  />
                </label>
              ) : laborProfiles.data?.length ? (
                <label className="mt-2 block space-y-1">
                  <span className="text-[11px] text-muted-foreground">Labor rate</span>
                  <Select
                    value={labor.laborRateProfileId ?? ''}
                    onValueChange={(laborRateProfileId) =>
                      updateConfig(index, {
                        ...config,
                        laborLines: config.laborLines!.map((line, i) =>
                          i === laborIndex ? { ...line, laborRateProfileId } : line,
                        ),
                      })
                    }
                  >
                    <SelectTrigger
                      className="h-8 text-xs"
                      aria-label={`${labor.label || `Labor ${laborIndex + 1}`} labor rate`}
                    >
                      <SelectValue placeholder="Choose a rate" />
                    </SelectTrigger>
                    <SelectContent>
                      {laborProfiles.data
                        .filter((profile) => profile.isActive)
                        .map((profile) => (
                          <SelectItem key={profile.id} value={profile.id}>
                            {profile.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </label>
              ) : null}

              {/* WHAT THIS HOUR IS CHARGED AT, where the field above cannot say
                  it. A PROFILE line bills the tiers frozen when the service was
                  materialized — the pricebook's current tiers are not what this
                  document quoted, and the profile picker only names the profile,
                  so the snapshot is the only account of the money.

                  A line billing its OWN rate has no second figure to state: it
                  charges the number in "Rate / hour", and printing the rate it
                  was materialized at beside it contradicted the field. Retyping
                  a $1,000 custom rate as $2,000 moved the line, the subtotal,
                  the cost and the margin panel, and left "Snapshot: $1,000.00/hr"
                  under the cursor — the one figure on screen still stating the
                  old rate, which reads as the edit having been ignored. Same
                  predicate the formula bills through, so what is shown as the
                  rate and what is charged as the rate cannot disagree. */}
              {!billsAtLineRate(labor) && labor.resolvedTiers?.length ? (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Snapshot:{' '}
                  {labor.resolvedTiers.map((tier) => `${formatCurrency(tier.rate)}/hr`).join(', ')}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-2">
          {/* WHAT THE SERVICE CONTAINS, at the price the CATALOG put on it.
              Not the document's part lines: the operator repriced the filter to
              $100 in the Materials section and this step said "$100.00 per
              each" for a part the pricebook priced at $16. Changing a quantity
              here changes the configuration, and the service reprices from it —
              which is exactly what editing a line must never do. */}
          {configuredParts.length ? (
            configuredParts.map((part, partIndex) => (
              <div
                key={part.itemId ?? `${part.name}-${partIndex}`}
                className="flex items-center gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{part.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(materializedUnitAmount(part))} each
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-8"
                    onClick={() => setConfiguredQuantity(partIndex, part.quantity - 1)}
                  >
                    −
                  </Button>
                  <NumberInput
                    aria-label={`${part.name} quantity`}
                    min={0}
                    className="h-8 w-20 text-center"
                    value={part.quantity}
                    emptyValue={0}
                    onChange={(next) => setConfiguredQuantity(partIndex, next ?? 0)}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-8"
                    onClick={() => setConfiguredQuantity(partIndex, part.quantity + 1)}
                  >
                    +
                  </Button>
                </div>
                {/* Out of the service altogether, which a quantity of 0 does
                    not say: a zeroed part still stands on the document as a
                    row describing work nobody is doing. */}
                {removeConfiguredPart ? (
                  <button
                    type="button"
                    aria-label={`Remove ${part.name} from this service`}
                    className="shrink-0 text-muted-foreground/50 hover:text-destructive"
                    onClick={() => removeConfiguredPart(partIndex)}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ))
          ) : (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No included parts for this service.
            </p>
          )}
          {/* A JOB IS NOT ALWAYS THE TEMPLATE. The catalog says what the service
              normally takes; this one needed a second fitting, and until now the
              only thing the step could do was re-count what the catalog already
              named. Adding here changes what the service CONTAINS, so it
              reprices — the same direction as the quantity controls above. */}
          {partPicker ? (
            <div className="space-y-1.5 pt-1">
              <EditorFieldLabel>Add a part this service does not include</EditorFieldLabel>
              {partPicker}
              <p className="text-xs text-muted-foreground">
                Its price goes inside the service, so the part bills nothing of its own.
              </p>
            </div>
          ) : null}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="max-w-xs space-y-1.5">
            <EditorFieldLabel>Job complexity adjustment</EditorFieldLabel>
            {/* The same control the pricing editor uses for a percentage:
                dragging shows what the adjustment is worth at a glance, and
                typing is still the way to hit an exact figure. */}
            <PercentSliderInput
              aria-label="Job complexity adjustment"
              value={config.complexityAdjustmentPercent ?? 0}
              onChange={(complexityAdjustmentPercent) =>
                updateConfig(index, { ...config, complexityAdjustmentPercent })
              }
            />
          </div>
          {config.afterHours?.enabled ? (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border p-2.5">
              <Checkbox
                checked={config.materializedAfterHours ?? false}
                onCheckedChange={(checked) =>
                  updateConfig(index, { ...config, materializedAfterHours: checked === true })
                }
              />
              <span>
                <span className="block text-xs font-medium">After-hours work</span>
                <span className="block text-xs text-muted-foreground">
                  Apply the service's{' '}
                  {config.afterHours.adjustmentType === 'PERCENT'
                    ? `${config.afterHours.value}%`
                    : formatCurrency(config.afterHours.value)}{' '}
                  adjustment.
                </span>
              </span>
            </label>
          ) : null}

          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setAdvancedOpen((open) => !open)}
          >
            <ChevronRight
              className={cn('size-4 transition-transform', advancedOpen && 'rotate-90')}
            />{' '}
            Advanced pricing settings
          </button>
          {advancedOpen && (
            <div className="space-y-4 rounded-lg border bg-muted/30 p-3">
              <div className="grid gap-3 md:grid-cols-2">
                {(config.laborLines ?? []).map((labor, laborIndex) => (
                  <div key={labor.id ?? laborIndex} className="space-y-1.5">
                    <EditorFieldLabel>
                      {labor.label || `Labor ${laborIndex + 1}`} rate
                    </EditorFieldLabel>
                    <Select
                      value={labor.laborRateProfileId ?? '__fixed'}
                      onValueChange={(profileId) => {
                        const profile = laborProfiles.data?.find(
                          (candidate) => candidate.id === profileId,
                        );
                        if (!profile) return;
                        updateConfig(index, {
                          ...config,
                          laborLines: config.laborLines!.map((line, i) =>
                            i === laborIndex
                              ? {
                                  ...line,
                                  rateSource: 'PROFILE',
                                  laborRateProfileId: profile.id,
                                  resolvedTiers: profile.tiers,
                                  resolvedRateName: profile.name,
                                }
                              : line,
                          ),
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {laborProfiles.data?.map((profile) => (
                          <SelectItem key={profile.id} value={profile.id}>
                            {profile.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              {updateMarkupProfile && markupProfiles.data?.length ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {(['MATERIAL', 'EQUIPMENT'] as const).map((target) => (
                    <div key={target} className="space-y-1.5">
                      <EditorFieldLabel>
                        {target === 'MATERIAL' ? 'Materials markup' : 'Equipment markup'}
                      </EditorFieldLabel>
                      <Select
                        value={
                          target === 'MATERIAL'
                            ? config.materialMarkupProfileId
                            : config.equipmentMarkupProfileId
                        }
                        onValueChange={(profileId) => {
                          const profile = markupProfiles.data?.find(
                            (candidate) => candidate.id === profileId,
                          );
                          if (profile) updateMarkupProfile(index, target, profile);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose profile" />
                        </SelectTrigger>
                        <SelectContent>
                          {markupProfiles.data.map((profile) => (
                            <SelectItem key={profile.id} value={profile.id}>
                              {profile.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {/* PROFIT — the labor equivalent of a markup profile, and the piece
              this panel was missing. Materials and equipment earn their margin
              from the profiles above; labor had no such control here, so the
              operator could see every cost of a dynamic service and none of
              what it makes. The same editor the pricebook uses. */}
          {/* Collapsed by default. Most of the time the uplift is already set
              and the operator is here for the duration or the parts; the
              headline figure is enough to see it is there, and opening is one
              click when it is not. */}
          <div className="rounded-lg border">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
              aria-expanded={upliftOpen}
              onClick={() => setUpliftOpen((open) => !open)}
            >
              <span className="text-sm font-medium">Profit uplift</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="tabular-nums">{formatCurrency(upliftRollup.surcharge)}</span>
                {upliftOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            </button>
            {upliftOpen ? (
              <div className="border-t p-3">
                <ProfitUpliftEditor
                  value={config}
                  onChange={(next) => updateConfig(index, next)}
                  amount={upliftRollup.surcharge}
                  base={upliftRollup.surchargeBase}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="rounded-lg bg-muted/60 p-4">
          {/* ONE number, DECOMPOSED — never a sum with the parts added back on
              top. The calculated price already contains labor, every part's
              marked-up contribution and the adjustments; listing the parts
              underneath it as an addition read $232 for a service priced at
              $216 and invited the operator to believe the catalog and the
              estimate disagreed. These rows split that one total into its two
              halves, so they foot to it rather than past it. */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Labor and adjustments</span>
            <span className="font-medium">{formatCurrency(laborAndAdjustments)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Included parts</span>
            <span className="font-medium">{formatCurrency(includedPartsTotal)}</span>
          </div>
          <div className="mt-3 flex items-center justify-between border-t pt-3">
            <span className="font-semibold">Service total</span>
            <span className="text-lg font-semibold">{formatCurrency(serviceTotal)}</span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            This configuration is saved as a snapshot on this document.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between border-t pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={step === 0}
          onClick={() => setStep((current) => Math.max(0, current - 1))}
        >
          Back
        </Button>
        {step < steps.length - 1 ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setStep((current) => Math.min(steps.length - 1, current + 1))}
          >
            Continue
          </Button>
        ) : (
          <Button type="button" size="sm" onClick={() => setEditing(false)}>
            Done
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The line editor, without a frame.
 *
 * Same component, `bodyOnly` — so the job sheet's scope table and the estimate
 * builder's card open into one editor rather than two that drift apart.
 */
export function LineItemDetailsEditor(
  props: Omit<Parameters<typeof MobileLineItemCard>[0], 'bodyOnly' | 'dragHandleProps'>,
) {
  return <MobileLineItemCard {...props} dragHandleProps={{}} bodyOnly />;
}

function MobileLineItemCard({
  item,
  lineItems,
  preview,
  pricingMode,
  index,
  estimateId,
  updateQuantity,
  updateName,
  updateType,
  updateUnit,
  updateBasePrice,
  updateManualPriceOverride,
  updatePriceStrategy,
  updateLaborRate,
  updateEstimatedHours,
  updateLaborCostLines,
  laborRateProfiles = [],
  updateDynamicPricingConfig,
  updateDynamicMarkupProfile,
  updateDescription,
  updateMarkupPercent,
  updateCostBasis,
  costSuppliers = [],
  updateCostSource,
  updateTaxable,
  updateTaxAccounting,
  updateMemberPricing,
  onSaveLineToPricebook,
  updateChargeable,
  updateHiddenFromCustomer,
  toggleOptional,
  toggleSelected,
  showServiceCallCredit,
  updateServiceCallCredit,
  removeLineItem,
  membershipDiscount,
  onPendingFilesChange,
  enableLineAttachments,
  customerId,
  serviceAddressId,
  updateEquipmentAssociation,
  equipmentInstallationUnits,
  updateEquipmentInstallationUnits,
  updateMembershipEquipmentAssociation,
  onConfigureMembership,
  membershipEquipmentPricingStatus = 'ready',
  componentPicker,
  removeConfiguredPart,
  sectioned = false,
  outOfSale = false,
  dragHandleProps,
  expanded,
  onToggle,
  bodyOnly = false,
}: {
  item: EstimateLineItem;
  lineItems: EstimateLineItem[];
  preview?: LineItemPreview;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  index: number;
  estimateId: string | undefined;
  updateQuantity: (index: number, qty: number) => void;
  updateName: (index: number, name: string) => void;
  updateType: (index: number, type: string) => void;
  updateUnit?: (index: number, unit: string) => void;
  updateBasePrice: (index: number, price: number) => void;
  /**
   * Price a MATERIAL / EQUIPMENT line for THIS document. `null` REMOVES the
   * override so the catalog price applies again. Omitted by surfaces that
   * cannot save it; the row control is hidden then.
   */
  updateManualPriceOverride?: (index: number, price: number | null) => void;
  updatePriceStrategy: (index: number, strategy: 'FLAT' | 'DYNAMIC') => void;
  updateLaborRate: (index: number, rate: number) => void;
  updateEstimatedHours: (index: number, estimatedHours: number) => void;
  /**
   * The labor rows on a FLAT service, with the hours and blended cost rate they
   * reduce to. Omitted by surfaces that cannot save them — the labor block is
   * hidden then rather than offering inputs that silently discard what is typed.
   */
  updateLaborCostLines?: (
    index: number,
    lines: ServiceLaborLine[],
    hours: number,
    cost: number,
  ) => void;
  /**
   * Configured rates the operator can fill the hourly COST from.
   *
   * Passed IN rather than fetched here: one query for the editor instead of one
   * per row, and a row stays renderable without a query client behind it.
   * Picking one SNAPSHOTS the number onto the line, so a later edit to the rate
   * can never reprice a document already saved.
   */
  laborRateProfiles?: LaborRateProfile[];
  updateDynamicPricingConfig?: (index: number, config: DynamicServicePricingConfig) => void;
  updateDynamicMarkupProfile?: (
    index: number,
    target: 'MATERIAL' | 'EQUIPMENT',
    profile: MarkupProfile,
  ) => void;
  updateDescription: (index: number, description: string) => void;
  updateMarkupPercent: (index: number, pct: number) => void;
  updateCostBasis: (index: number, cost: number) => void;
  /** Job-only sourcing for a hand-typed MATERIAL/EQUIPMENT line. */
  costSuppliers?: Supplier[];
  updateCostSource?: (index: number, supplier: Pick<Supplier, 'id' | 'name'> | null) => void;
  updateTaxable: (index: number, taxable: boolean) => void;
  /** Custom lines only: the tax class and GL accounts the line states. */
  updateTaxAccounting?: (index: number, patch: LineTaxAccounting) => void;
  /** Custom lines only: what a member pays for this line. */
  updateMemberPricing?: (index: number, patch: LineMemberPricing) => void;
  /**
   * Custom lines only: point the line at the catalog item it just became.
   * Keyed by row id — the list stays sortable while the create is in flight.
   */
  onSaveLineToPricebook?: (
    rowId: string,
    item: { id: string; code: string; itemVersion?: number },
  ) => void;
  /** Omitted by surfaces that do not edit billing; the control is hidden then. */
  updateChargeable?: (index: number, chargeable: boolean) => void;
  /** Omitted by surfaces that cannot save it; the eye is hidden then. */
  updateHiddenFromCustomer?: (index: number, hidden: boolean) => void;
  toggleOptional: (index: number) => void;
  toggleSelected: (index: number) => void;
  showServiceCallCredit?: boolean;
  updateServiceCallCredit?: (index: number, credit: boolean) => void;
  removeLineItem: (index: number) => void;
  membershipDiscount: MembershipDiscountInfo | null;
  onPendingFilesChange?: (rowId: string, files: File[]) => void;
  enableLineAttachments: boolean;
  customerId?: string | null;
  serviceAddressId?: string | null;
  componentPicker?: (parent: EstimateLineItem, parentIndex: number) => ReactNode;
  removeConfiguredPart?: (index: number, partIndex: number) => void;
  updateEquipmentAssociation?: (
    index: number,
    equipmentIds: string[],
    locationOverrideReason?: string,
  ) => void;
  /** Job-only per-unit installation facts, persisted through the equipment API. */
  equipmentInstallationUnits?: JobEquipmentInstallationUnitDraft[];
  updateEquipmentInstallationUnits?: (units: JobEquipmentInstallationUnitDraft[]) => void;
  updateMembershipEquipmentAssociation?: (
    index: number,
    equipmentIds: string[],
    locationOverrideReason: string | undefined,
    selectedEquipment: Equipment[],
  ) => void;
  onConfigureMembership?: (index: number) => void;
  membershipEquipmentPricingStatus?: 'ready' | 'loading' | 'error';
  /** The row sits under a type-scoped heading, so its type is settled. */
  sectioned?: boolean;
  /** Out of the sale by its SERVICE's choice, not its own — drawn dimmed. */
  outOfSale?: boolean;
  dragHandleProps: HTMLAttributes<HTMLButtonElement>;
  expanded: boolean;
  onToggle: () => void;
  /**
   * Render only the details editor — no card, no header row.
   *
   * The job-create sheet frames its scope as a table and supplies its own
   * collapsed row; what a line opens into is this editor either way.
   */
  bodyOnly?: boolean;
}) {
  const isCustomLine = item.isCustom ?? !item.code;
  const resolvedUnitPrice = unitPriceOf(item);
  const [qtyInput, setQtyInput] = useState(String(item.quantity));
  const [costInput, setCostInput] = useState(item.costBasis ? String(item.costBasis) : '');
  const [markupInput, setMarkupInput] = useState(
    item.markupPercent ? String(item.markupPercent) : '',
  );
  // The RESOLVED price — an override if this document carries one, the catalog
  // price otherwise — so an overridden line shows what it actually charges
  // rather than a stale pricebook figure.
  const [priceInput, setPriceInput] = useState(String(resolvedUnitPrice));

  // A parent may reject a guarded edit (for example, an estimate-origin line
  // while composing a job) and re-issue the unchanged row object. Re-sync the
  // local numeric drafts when that row is refreshed so cancelled edits never
  // remain visually applied.
  // Synchronize each draft only from the committed value it edits. Cost
  // keystrokes update markup live, which replaces the parent row object; one
  // broad `[item]` effect therefore reset the uncommitted cost after every
  // digit and made the number field appear to accept only `e` or `.`. Keeping
  // these independent preserves empty, decimal and trailing-dot drafts while
  // still reflecting an accepted/rejected parent update after blur.
  useEffect(() => setQtyInput(String(item.quantity)), [item.quantity]);
  useEffect(() => setCostInput(item.costBasis ? String(item.costBasis) : ''), [item.costBasis]);
  useEffect(
    () => setMarkupInput(item.markupPercent ? String(item.markupPercent) : ''),
    [item.markupPercent],
  );
  useEffect(() => setPriceInput(String(resolvedUnitPrice)), [resolvedUnitPrice]);

  // ONE number per line: what this row shows IS what it contributes to the
  // subtotal. The pricing preview's answer, which is the same figure the
  // subtotal, the tax beside it and the operations engine all use.
  //
  // A dynamic service used to be special-cased here into `basePrice` plus every
  // child part's price, to stand in for a bundle. It cannot: a part included in
  // its service is already paid for INSIDE that price and charges nothing of
  // its own (the double-charging rule), so adding it back stated a service at
  // its price plus a part the customer is not billed for again — $210 on a row
  // whose tax, subtotal and invoice all said $135, and $232.00 against a service
  // the pricebook priced and the document totalled at $216.00. The parts' markup
  // is already inside the calculated price (the owner's ruling,
  // SPEC-line-item-model), so adding it again stated the same money twice and
  // left the operator unable to tell which figure was real.
  //
  // Reading the preview also fixes the roll-up reading raw `basePrice`, where a
  // manual override, a declined optional part or a discount moved the bill
  // without moving the row.
  const effectiveSubtotal =
    preview?.lineAfterDiscount ??
    (preview
      ? Math.max(
          0,
          preview.lineSubtotal -
            (preview.membershipDiscountAmount ?? 0) -
            (preview.documentDiscountAmount ?? 0),
        )
      : unitPriceOf(item) * item.quantity);
  const effectiveTax = preview?.lineTax ?? 0;
  const normalizedType = normalizeLineItemType(item.type);
  // Cost + markup is how a thing you buy and resell is priced, and only a part
  // is bought and resold. A service costs its TIME — the labor block below —
  // and `document-costing` never reads a unit cost off one, so offering the
  // pair on a service accepted a cost nothing counted: the row said "Cost not
  // set" and the margin read 100% (NOT-779). Sectioned or flat, the rule is the
  // same one.
  const showCostMarkupFields = sectionForType(item.type) !== 'services';
  // Only a part can have its cost recovered through something else, so only a
  // part is offered the chargeable control.
  const isPartLine = normalizedType === 'MATERIAL' || normalizedType === 'EQUIPMENT';
  // A part's price is edited on the row itself — the operator prices parts far
  // more often than anything else in the per-line editor, and having to expand
  // a row to see the number the customer is being charged is what made the
  // collapsed row's static amount unhelpful.
  const showRowPrice = isPartLine && Boolean(updateManualPriceOverride);
  // Priced at something other than the catalog. An override EQUAL to the
  // catalog price is not worth marking — there is nothing to revert to.
  const rowPriceOverridden = isManualPriceOverride(item);
  // Does this line contribute revenue? THE canonical answer (line-economics),
  // and the only one this row is allowed to have: the badge, the amount, the
  // price controls and the Chargeable checkbox all read it, so a row can never
  // contradict itself. Note it is the RELATIONSHIP's treatment that decides —
  // a part inherited as `included_in_service` charges nothing however the
  // catalog's own `chargeable` flag reads.
  const billed = chargesRevenue(item);
  // A standalone unbilled part is already included in the required work.
  // Offering it as optional would let selection remove its real cost from the
  // job. A linked component may still be optional within its parent, and a
  // billed line with a literal $0 price is still a valid offer.
  const offered = (billed || Boolean(item.parentLineKey)) && item.isOptional;
  // What the row prints where an amount is TEXT. A line that charges nothing
  // has no price to state — a dash, not a claim of $0.00. The editable price
  // field beside it is untouched: the stored price is still real.
  const lineAmountText = internalPriceDisplay(item, effectiveSubtotal, formatCurrency);
  // Internal part rows state BOTH sides of their economics. An included part's
  // document-line price is $0 — its catalog selling value is already recovered
  // through the parent — while its cost remains real and still moves margin.
  // Explicit labels avoid the old `— · $1,500 cost` shorthand, which made it
  // impossible to tell whether the missing figure was price, cost or loading.
  const lineCostText = isPartLine ? formatCurrency(unitCostOf(item) * item.quantity) : null;
  // Sectioned, a part is drawn under its own kind's heading rather than inside
  // the service's card, so the row itself has to name the service it belongs to
  // — the card's left rule used to say that and no longer can.
  const parentService = item.parentLineKey
    ? lineItems.find((line) => !line.parentLineKey && line.lineKey === item.parentLineKey)
    : undefined;
  const markupUnavailable =
    showCostMarkupFields && !isMarkupAvailable(parseDraftNumber(costInput, item.costBasis ?? 0));
  const markupHelpId = `line-item-markup-help-${item._rowId}`;

  useEffect(() => {
    if (!markupUnavailable || (item.markupPercent ?? 0) === 0) return;
    updateMarkupPercent(index, 0);
    setMarkupInput('');
  }, [index, item.markupPercent, markupUnavailable, updateMarkupPercent]);

  // LABOR AS COST on a FLAT service. Offered only where the price is typed —
  // a dynamically priced service already states its hours where they earn
  // money, and a second input would be a second answer.
  //
  // `pricesFromHours` is THE question ("do this line's hours drive its price"),
  // asked rather than re-derived, so the two blocks can never both appear.
  const isFlatPricedService =
    isTMOverrideEligibleType(item.type) &&
    !pricesFromHours(
      { type: item.type, priceStrategy: item.priceStrategy, isCustom: isCustomLine },
      pricingMode,
    ) &&
    !item.dynamicPricingConfig?.enabled;
  const showFlatLaborCost =
    isFlatPricedService && !item.parentLineKey && Boolean(updateLaborCostLines);

  const tmModeLabel = getDynamicPricingLabel(
    { type: item.type, priceStrategy: item.priceStrategy, isCustom: isCustomLine },
    pricingMode,
  );
  const tmIncompleteReason = getDynamicPricingIncompleteReason({
    type: item.type,
    priceStrategy: item.priceStrategy,
    pricingMode,
    isCustom: isCustomLine,
    laborRate: item.laborRate,
    estimatedHours: item.estimatedHours,
    dynamicPricingConfig: item.dynamicPricingConfig,
  });
  const isMembershipLine = isMembershipLineType(item.type);
  const hasLegacyMembershipQuantity = isMembershipLine && item.quantity !== 1;
  const dimmed = (offered && !item.isSelected) || outOfSale;
  const headerPressRef = useRef(false);

  /**
   * The row's price edit. Writes `manualPriceOverride` — the price used on THIS
   * document — and never `basePrice`, which is the pricebook's original and the
   * only thing a revert can put back.
   *
   * An EMPTY field removes the override rather than pricing the line at zero:
   * "no override" and "$0" are different facts, and a chargeable $0 part is a
   * legitimate thing to quote.
   */
  function commitRowPrice() {
    if (isMembershipLine) return;
    if (!updateManualPriceOverride) return;
    const raw = priceInput.trim();
    if (raw === '') {
      // Clearing removes the override — but only where one can exist. A custom
      // line has no catalog original to fall back to, so there is nothing to
      // revert TO and the field simply redraws its own price.
      if (!item.isCustom) updateManualPriceOverride(index, null);
      setPriceInput(String(basePriceOf(item)));
      return;
    }
    const committed = Math.max(0, parseDraftNumber(raw, unitPriceOf(item)));
    // Through the same writer as the expanded editor's price field. Writing the
    // override directly here treated a hand-typed part like a catalog one, and
    // that is precisely the line whose `memberPrice` is a fixed price rather
    // than the numerator of a rate — so repricing a custom part from this row
    // moved the member's total off the figure that was configured for them.
    writeUnitPrice(committed);
    setPriceInput(String(committed));
  }

  function revertRowPrice() {
    if (isMembershipLine) return;
    if (!updateManualPriceOverride) return;
    updateManualPriceOverride(index, null);
    setPriceInput(String(basePriceOf(item)));
  }

  /**
   * WHERE a price the operator sets is written: `manualPriceOverride`.
   *
   * NEVER `basePrice`. That field is the pricebook's original — the only thing
   * "revert to the catalog price" can put back, and the DENOMINATOR of every
   * member rate. Front and back both scale the resolved amount by the catalog
   * ratio (`computeMembershipDiscount` in `lib/sales/estimate-pricing-preview`,
   * `withItemMemberBasis` in operations' `invoices.service`):
   * `memberTotal = amount x memberPrice / basePrice`. Typing over `basePrice`
   * moved that denominator, so with catalog `memberPrice 120 / basePrice 150` —
   * a 20% member rate — quantity 2 and $200/unit typed here, this editor
   * discounted $160 and billed the member $120/unit while the job and invoice
   * editors, which write the override, discounted $80 and billed $160/unit. One
   * operator action, two bills, $40/unit apart. SPEC-line-item-model.md,
   * "Member pricing — direction decided": the member rate applies to the FINAL
   * price.
   *
   * Nothing a NON-member is charged moves. `unitPriceOf` resolves
   * `manualPriceOverride ?? unitPrice ?? price ?? basePrice`, and the operations
   * engine's `resolveUnitPrice` has the same order, so the same $200 is simply
   * read from the field that already outranks. It also ends a contradiction on
   * a part: the collapsed row's price field has always written the override, so
   * a part carrying one swallowed everything typed into this control — the
   * number went into `basePrice`, the override still won, and the price on
   * screen never moved.
   *
   * `updateManualPriceOverride` is OPTIONAL, and its absence is meaningful: this
   * component also edits proposal options, job types and estimate TEMPLATES,
   * whose state has no override field because their `basePrice` IS the price
   * they are authoring — there is no catalog original of their own to protect
   * and no member rate to scale. Those surfaces keep writing `basePrice`.
   */
  function writeUnitPrice(price: number) {
    // A HAND-TYPED line has no catalog original, so `basePrice` IS its price —
    // the same reason the template surfaces below keep writing it. That is not
    // cosmetic here: on such a line `memberPrice` is the member's FIXED unit
    // price (the control is literally labelled "Member price"), and the only
    // thing making the shared ratio express that is `basePrice` tracking the
    // typed price, so `amount x memberPrice / basePrice` reduces to
    // `memberPrice`. Freeze `basePrice` at the original and it stops reducing:
    // a line typed at $100 with a fixed member price of $80, repriced to $120,
    // would quote the member $120 x 80/100 = $96 instead of the $80 configured.
    //
    // A catalog line is the opposite case: `basePrice` and `memberPrice` are
    // BOTH the pricebook's, so their ratio is a real member RATE and has to
    // survive the operator pricing the line over the catalog. That is the bug
    // this function exists to fix.
    if (updateManualPriceOverride && !item.isCustom) {
      updateManualPriceOverride(index, price);
      return;
    }
    updateBasePrice(index, price);
  }

  function commitBasePrice(price: number) {
    writeUnitPrice(price);
    if (!showCostMarkupFields) return;

    const cost = parseDraftNumber(costInput, item.costBasis ?? 0);
    if (cost <= 0) {
      // No cost basis → markup is undefined; clear it instead of leaving a
      // stale value that a later cost/markup edit would recompute from.
      if ((item.markupPercent ?? 0) !== 0) {
        updateMarkupPercent(index, 0);
        setMarkupInput('');
      }
      return;
    }

    // Allow a negative margin (price < cost) — consistent with the formula and
    // the backend's markup ≥ -100 rule (NOT-96) — instead of clamping to 0.
    const nextMarkup = markupFromCostPrice(cost, price);
    if (nextMarkup === null) return;
    updateMarkupPercent(index, nextMarkup);
    setMarkupInput(nextMarkup === 0 ? '' : String(nextMarkup));
  }

  function syncMarkupFromCost(cost: number) {
    if (!showCostMarkupFields) return;
    if (cost <= 0) {
      // No cost basis → markup is meaningless. Clear it unconditionally (keep
      // the flat price) — the committed markupPercent may read 0 while the
      // draft input still shows a stale value, so clear the draft regardless.
      // (NOT-241)
      updateMarkupPercent(index, 0);
      setMarkupInput('');
      return;
    }

    // Against the price this line SELLS AT, not the catalog's. Markup is the
    // distance between cost and the money being charged; reading `basePrice`
    // here reported the catalog's distance on a line priced over it — cost $100
    // on a $200 line showed 50% because the pricebook says $150.
    const nextMarkup = markupFromCostPrice(cost, unitPriceOf(item));
    if (nextMarkup === null) return;
    updateMarkupPercent(index, nextMarkup);
    setMarkupInput(nextMarkup === 0 ? '' : String(nextMarkup));
  }

  function commitCostBasis(cost: number) {
    updateCostBasis(index, cost);
    setCostInput(cost === 0 ? '' : String(cost));
    syncMarkupFromCost(cost);
  }

  function commitMarkupPercent(markup: number) {
    updateMarkupPercent(index, markup);
    setMarkupInput(markup === 0 ? '' : String(markup));
    if (!showCostMarkupFields) return;

    const cost = parseDraftNumber(costInput, item.costBasis ?? 0);
    // Markup has no cost to apply to → leave the flat price untouched.
    if (cost <= 0) return;

    // Setting a markup is the operator saying how this line is priced, so the
    // price it computes goes where every other price this editor sets goes.
    //
    // This used to write `basePrice` and then CLEAR any override, because a
    // typed price outranks the computed one everywhere downstream (`unitPriceOf`,
    // and the engine's `resolveUnitPrice`) and would otherwise swallow the markup
    // edit whole — the percentage went in, the price it computed was written,
    // and nothing moved. Writing the computed price to the override itself
    // replaces the earlier number in place, so there is nothing left to clear:
    // the clearing step only existed to get out of the way of a field this no
    // longer writes.
    writeUnitPrice(unitPriceFromCostMarkup(cost, markup));
  }

  /*
   * Identity: what this line IS — its name, its kind, its unit of measure.
   *
   * These live in the header, which the `bodyOnly` frame does not draw. A
   * caller that supplies its own header row therefore has to be given them
   * somewhere, or a custom line added on that surface can never be renamed and
   * no line can change its UOM. Same controls, same handlers — only the place
   * they are drawn moves.
   */
  const identityFields =
    isCustomLine || (updateUnit && !isMembershipLine) ? (
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        {isCustomLine ? (
          <>
            <div className="space-y-1.5">
              <EditorFieldLabel>Item name</EditorFieldLabel>
              <Input
                value={item.name}
                onChange={(event) => updateName(index, event.target.value)}
                placeholder="Custom item name"
                className="h-8 w-60 bg-background text-sm"
              />
            </div>
            {!sectioned ? (
              <div className="space-y-1.5">
                <EditorFieldLabel>Kind</EditorFieldLabel>
                <Select
                  value={normalizeLineItemType(item.type) || 'SERVICE'}
                  onValueChange={(type) => updateType(index, type)}
                >
                  <SelectTrigger className="h-8 w-32 bg-background text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SERVICE">Service</SelectItem>
                    <SelectItem value="MATERIAL">Material</SelectItem>
                    <SelectItem value="EQUIPMENT">Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </>
        ) : null}
        {updateUnit && !isMembershipLine ? (
          <div className="space-y-1.5">
            <EditorFieldLabel>Unit</EditorFieldLabel>
            <UnitSelect value={item.unit ?? ''} onChange={(unit) => updateUnit(index, unit)} />
          </div>
        ) : null}
      </div>
    ) : null;

  const detailsBody = (
    <>
      {/* THE WAY BACK. A materialized service is edited through the wizard
              below rather than through a price field, so the toggle that lived
              beside "Unit price" is not drawn for it — and switching to Dynamic
              now materializes, which meant one click took the operator somewhere
              with no Flat button in sight. The dormant typed price is kept
              precisely so they can come back to it (see `updatePriceStrategy`),
              and a control nothing renders is a price they cannot reach. */}
      {item.dynamicPricingConfig?.enabled &&
      isTMOverrideEligibleType(item.type) &&
      pricingMode === 'STANDARD' ? (
        <div className="flex items-center justify-between gap-2">
          <EditorFieldLabel>How this service is priced</EditorFieldLabel>
          <PriceStrategyToggle
            value={(item.priceStrategy ?? 'FLAT') as PriceStrategy}
            onChange={(s) => updatePriceStrategy(index, s)}
          />
        </div>
      ) : null}
      {item.dynamicPricingConfig?.enabled && updateDynamicPricingConfig ? (
        <DynamicServiceCustomizer
          item={item as EstimateLineItem & { dynamicPricingConfig: DynamicServicePricingConfig }}
          lineItems={lineItems}
          index={index}
          updateConfig={updateDynamicPricingConfig}
          updateMarkupProfile={updateDynamicMarkupProfile}
          updateQuantity={updateQuantity}
          partPicker={componentPicker?.(item, index)}
          removeConfiguredPart={
            removeConfiguredPart && ((partIndex) => removeConfiguredPart(index, partIndex))
          }
        />
      ) : null}
      {!item.dynamicPricingConfig?.enabled && (
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-[repeat(auto-fill,15rem)]">
          {/* PRICE, only where something is charged. A line recovered
                  through the service that carries it has no price to set and no
                  markup to state — the same rule the pricebook applies to a
                  component it does not bill. COST is not conditional: what we
                  paid is true either way, and it is the only figure the margin
                  can be read from here. */}
          {billed ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <EditorFieldLabel>Unit price</EditorFieldLabel>
                {/* Only the pricebook can price dynamically. A hand-typed
                        line has no catalog row to hold a labor rate, a markup
                        profile or a dynamic configuration, so offering it the
                        choice offered a price nothing could compute — see
                        `allowsDynamicPricing`. */}
                {allowsDynamicPricing({ type: item.type, isCustom: isCustomLine }) &&
                  pricingMode === 'STANDARD' && (
                    <PriceStrategyToggle
                      value={(item.priceStrategy ?? 'FLAT') as PriceStrategy}
                      onChange={(s) => updatePriceStrategy(index, s)}
                    />
                  )}
              </div>
              <LineItemPricingControls
                className="w-full"
                type={item.type}
                priceStrategy={item.priceStrategy}
                pricingMode={pricingMode}
                isCustom={isCustomLine}
                // The price this line SELLS at — the override when this
                // document carries one, the catalog's otherwise. Showing
                // `basePrice` put the pricebook's figure in a field over a
                // line billing something else, and the first keystroke in it
                // then repriced the line from a number the operator never
                // chose.
                basePrice={unitPriceOf(item)}
                laborRate={item.laborRate}
                estimatedHours={item.estimatedHours}
                onUpdateBasePrice={commitBasePrice}
                onUpdateLaborRate={(r) => updateLaborRate(index, r)}
                onUpdateEstimatedHours={(h) => updateEstimatedHours(index, h)}
              />
            </div>
          ) : (
            <p className="text-xs leading-5 text-muted-foreground sm:col-span-full">
              Not billed on its own line — its cost is recovered through the service that carries
              it.
              {/* Only where this surface can actually undo it: pointing at
                      a control that is not on the screen is worse than saying
                      nothing. */}
              {updateChargeable ? ' Tick Chargeable below to price it.' : ''}
            </p>
          )}

          {showCostMarkupFields ? (
            <>
              {isCustomLine && isPartLine && updateCostSource ? (
                <div className="space-y-1.5">
                  <EditorFieldLabel>Cost source</EditorFieldLabel>
                  <Select
                    value={
                      item.costSource === 'SUPPLIER' && item.costSupplierId
                        ? item.costSupplierId
                        : '__direct_cost__'
                    }
                    onValueChange={(value) => {
                      if (value === '__direct_cost__') {
                        updateCostSource(index, null);
                        return;
                      }
                      const supplier = costSuppliers.find((candidate) => candidate.id === value);
                      if (supplier) {
                        updateCostSource(index, { id: supplier.id, name: supplier.name });
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 bg-background text-xs">
                      <SelectValue placeholder="Choose cost source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__direct_cost__">Direct cost (no supplier)</SelectItem>
                      {costSuppliers.map((supplier) => (
                        <SelectItem
                          key={supplier.id}
                          value={supplier.id}
                          disabled={
                            supplier.isActive === false && supplier.id !== item.costSupplierId
                          }
                        >
                          {supplier.name}
                          {supplier.isActive === false ? ' (inactive)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {item.costSource === 'SUPPLIER' && item.costSupplierName
                      ? `Snapshot the unit cost quoted by ${item.costSupplierName}.`
                      : 'Enter a raw unit cost without linking a supplier.'}
                  </p>
                </div>
              ) : null}
              <div className="space-y-1.5">
                <EditorFieldLabel>
                  {isCustomLine && item.costSource === 'SUPPLIER'
                    ? 'Supplier unit cost'
                    : 'Unit cost'}
                </EditorFieldLabel>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={costInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // A native number input treats `e` as an exponent and can
                    // collapse transitional values such as `10.` to empty.
                    // Cost is non-negative decimal money: preserve the raw
                    // editing states we support and reject every other token.
                    if (raw !== '' && !/^\d*(?:\.\d*)?$/.test(raw)) return;
                    setCostInput(raw);
                    // Keep Markup in sync while the user types, matching the
                    // live Unit Price behavior. Cost itself is still committed
                    // on blur so partial drafts such as "10." remain editable.
                    syncMarkupFromCost(parseDraftNumber(raw));
                  }}
                  onBlur={() => {
                    const committed = Math.max(0, parseDraftNumber(costInput));
                    commitCostBasis(committed);
                  }}
                  placeholder="0.00"
                  className={`h-9 bg-background text-xs ${INPUT_CLS}`}
                />
              </div>

              {/* Markup is the distance between cost and PRICE. With no
                      price being charged there is no such distance to state,
                      and a percentage over an amount nobody pays reads as a
                      margin the job does not earn. */}
              {billed ? (
                <div className="space-y-1.5">
                  <EditorFieldLabel>Markup %</EditorFieldLabel>
                  <Input
                    type="number"
                    min={-100}
                    step="any"
                    value={markupUnavailable ? '' : markupInput}
                    disabled={markupUnavailable}
                    aria-describedby={markupUnavailable ? markupHelpId : undefined}
                    onChange={(e) => {
                      // NOT-241: recompute Unit Price live while typing. Keep the
                      // markup draft raw (allows "-", "10.", …) — onBlur clamps to
                      // ≥ −100 (NOT-96).
                      const raw = e.target.value;
                      setMarkupInput(raw);
                      if (!showCostMarkupFields) return;
                      const cost = parseDraftNumber(costInput, item.costBasis ?? 0);
                      if (cost > 0) {
                        // Same destination as the committed edit below — a
                        // keystroke and a blur may not write different
                        // fields, or the line prices one way while it is
                        // being typed and another the moment focus leaves.
                        writeUnitPrice(unitPriceFromCostMarkup(cost, parseDraftNumber(raw)));
                      }
                    }}
                    onBlur={() => {
                      const committed = Math.max(-100, parseDraftNumber(markupInput));
                      commitMarkupPercent(committed);
                    }}
                    placeholder={markupUnavailable ? 'N/A' : '0'}
                    className={`h-9 bg-background text-xs ${INPUT_CLS}`}
                  />
                  {markupUnavailable ? (
                    <p
                      id={markupHelpId}
                      className="flex items-start gap-1 text-xs text-muted-foreground"
                    >
                      <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                      <span>Markup is unavailable when Unit Cost is $0.00.</span>
                    </p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      )}

      {/*
        Sized against THIS row, not the viewport. The split used to be `md:`,
        which turns on at a 768px window regardless of how little room the row
        actually has — and this editor renders inside the settings workspace,
        behind the app sidebar, the settings nav, the card padding and the cost
        rail. On a 1280px screen that left roughly 340px, of which Tax took a
        fixed 15rem, so Description got ~85px and a textarea in an 85px box
        breaks mid-word: "Mau / ricio / Mac / ri".

        Tax holds a read-only chip that already truncates, so it is capped
        rather than fixed, and below 32rem the two fields stack full-width
        instead of Description being starved to nothing.
      */}
      {/* The container has to be a WRAPPER: an element with container-type
          cannot query itself, only its descendants can query it. Both classes
          on one div would have left the grid answering to some outer container
          — or to none. */}
      <div className="@container">
        <div className="grid gap-x-6 gap-y-4 @lg:grid-cols-[minmax(0,1fr)_minmax(6rem,9rem)]">
          <div className="space-y-1.5">
            <EditorFieldLabel>Description</EditorFieldLabel>
            <Textarea
              rows={2}
              value={item.description ?? ''}
              onChange={(e) => updateDescription(index, e.target.value)}
              placeholder="Add description…"
              className="min-h-0 resize-none bg-background text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <EditorFieldLabel>Tax</EditorFieldLabel>
            {/* Read-only value today; styled as a select slot so a future tax
                  selector (per-line tax code) can drop in without re-layout. */}
            <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-xs font-medium">
              <span className="truncate">
                {item.taxable && effectiveTax > 0 ? `+${formatCurrency(effectiveTax)}` : '—'}
              </span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
            </div>
          </div>
        </div>
      </div>

      {updateEquipmentInstallationUnits && normalizeLineItemType(item.type) === 'EQUIPMENT' ? (
        <JobEquipmentInstallationFields
          lineId={item._rowId}
          lineName={item.name}
          quantity={item.quantity}
          units={equipmentInstallationUnits}
          onChange={updateEquipmentInstallationUnits}
        />
      ) : updateEquipmentAssociation ? (
        normalizeLineItemType(item.type) === 'EQUIPMENT' ? (
          <div className="rounded-md border border-dashed bg-background/70 p-3 text-xs text-muted-foreground">
            Each physical unit on this equipment line will require its own serial number and
            installation confirmation before the job can be completed.
          </div>
        ) : isMembershipLine && membershipEquipmentPricingStatus !== 'ready' ? (
          <div className="flex items-center gap-2 rounded-md border border-dashed bg-background/70 p-3 text-xs text-muted-foreground">
            {membershipEquipmentPricingStatus === 'loading' ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <AlertTriangle className="size-3.5 text-warning" />
            )}
            {membershipEquipmentPricingStatus === 'loading'
              ? 'Loading membership pricing before equipment can be changed…'
              : 'Membership pricing could not be loaded. Reload before changing equipment.'}
          </div>
        ) : (
          <EquipmentLineAssociation
            customerId={customerId}
            serviceAddressId={serviceAddressId}
            equipmentIds={item.equipmentIds ?? []}
            locationOverrideReason={item.equipmentLocationOverrideReason}
            hideCrossCustomerSearch={isMembershipLine}
            requireCompleteSelection={isMembershipLine}
            title={
              isMembershipLine
                ? 'Equipment covered by this membership'
                : 'Existing equipment serviced'
            }
            onChange={(equipmentIds, reason, selectedEquipment = []) => {
              if (isMembershipLine) {
                updateMembershipEquipmentAssociation?.(
                  index,
                  equipmentIds,
                  reason,
                  selectedEquipment,
                );
                return;
              }
              updateEquipmentAssociation(index, equipmentIds, reason);
            }}
          />
        )
      ) : null}

      {/* LABOR ON A FLAT SERVICE — a peer of the parts block below, and
              deliberately drawn the same way, because it is the same kind of
              thing: something the service consumes, which costs money and does
              NOT move the price. The operator sets the price; this records what
              delivering it takes.

              Only where the price is FLAT. On a dynamic service the hours drive
              the price and belong with the pricing controls above — two hours
              inputs on one line would leave the operator guessing which one the
              customer is paying for. */}
      {/* LABOR ON A FLAT SERVICE — a peer of the parts block below, and
              deliberately drawn the same way, because it is the same kind of
              thing: something the service consumes, which costs money and does
              NOT move the price. The operator sets the price; this records what
              delivering it takes.

              Only where the price is FLAT. On a dynamic service the hours drive
              the price and belong with the pricing controls above — two hours
              inputs on one line would leave the operator guessing which one the
              customer is paying for.

              The same block the job's line editor draws, and the same rows,
              heading and copy the PRICEBOOK form uses, so one idea never grows
              two names. */}
      {showFlatLaborCost && (
        <ServiceLaborCostEditor
          line={item}
          profiles={laborRateProfiles}
          onChange={(lines, hours, cost) => updateLaborCostLines?.(index, lines, hours, cost)}
        />
      )}

      {/* NO parts picker here, deliberately.
              What a service consumes is decided in the PRICEBOOK, where the
              service is built. On the estimate those parts arrive as ordinary
              lines filed under Materials and Equipment — their own sections,
              each with its own search — so adding them from inside the service
              row was a second way to do the same thing, in the one place where
              the parts are not shown. */}

      {/* WHAT A MEMBER PAYS — the pricebook's per-item member pricing, on a
              line with no catalog item to have carried it.

              Only where something is charged: a member price on a line that
              bills nothing is a discount off an amount nobody pays. Custom
              lines only, for the same reason as the block below — a picked line
              already brought the catalog's answer with it. */}
      {isCustomLine && updateMemberPricing && billed ? (
        <div className="rounded-md border border-dashed bg-background/70 p-3">
          <LineMemberPricingFields
            value={item}
            onChange={(patch) => updateMemberPricing(index, patch)}
          />
        </div>
      ) : null}

      {/* HOW THIS LINE IS TAXED, AND WHERE ITS MONEY POSTS — the pricebook
              form's "Tax & Accounting" fields, on the one kind of line with no
              catalog row behind it to have answered them already.

              Custom lines only. A picked line arrives with all of this
              snapshotted from its pricebook item, and the pricebook is where it
              is meant to be changed; asking again per estimate would let the two
              disagree with nothing to say which was meant. A hand-typed line has
              no such source, and unasked it reached the ledger classified by a
              guess from its bare type and posted to no account at all. */}
      {isCustomLine && updateTaxAccounting ? (
        <LineTaxAccountingFields
          itemType={item.type}
          taxable={item.taxable ?? true}
          value={item}
          onChange={(patch) => updateTaxAccounting(index, patch)}
        />
      ) : null}

      {/* KEEP IT. Everything above describes an item that exists only on
              this estimate — its price, its cost, how it is taxed, where its
              money posts. Saving it to the pricebook is what stops the next
              estimate having to be told all of it again. */}
      {isCustomLine && onSaveLineToPricebook ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-dashed bg-background/70 p-3">
          <p className="text-xs text-muted-foreground">Keep this item for next time.</p>
          <SaveLineToPricebookButton
            line={item}
            onSaved={(created) => onSaveLineToPricebook(item._rowId, created)}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-3 border-t border-border/70 pt-3 sm:flex-row sm:items-start sm:justify-between">
        {/* The pricebook-taxability hint sits *below* the toggle row, not inside
                it — nesting it under the Taxable label made that one control taller
                than its siblings and knocked every checkbox out of alignment. */}
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-4 text-xs">
            {isMembershipLine || billed || item.parentLineKey ? (
              <label
                className={cn(
                  'flex items-center gap-2 select-none',
                  isMembershipLine && item.membershipCustomerChoosesBillingOption === true
                    ? 'cursor-default'
                    : 'cursor-pointer',
                )}
              >
                <Checkbox
                  checked={
                    isMembershipLine && item.membershipCustomerChoosesBillingOption === true
                      ? true
                      : item.isOptional
                  }
                  disabled={
                    isMembershipLine && item.membershipCustomerChoosesBillingOption === true
                  }
                  onCheckedChange={() => toggleOptional(index)}
                />
                <span className="text-muted-foreground">
                  {isMembershipLine && item.membershipCustomerChoosesBillingOption === true
                    ? 'Optional · customer decides'
                    : 'Optional'}
                </span>
              </label>
            ) : null}
            <label className="flex cursor-pointer select-none items-center gap-2">
              <Checkbox
                checked={item.taxable ?? true}
                aria-describedby={
                  item.pricebookTaxable === false
                    ? `pricebook-taxability-${item._rowId}`
                    : undefined
                }
                onCheckedChange={(checked) => updateTaxable(index, checked === true)}
              />
              <span className="text-muted-foreground">Taxable</span>
            </label>
            {/* Only parts can be recovered through something else. A
                    service, bundle or membership IS what the customer is
                    buying, so it is always charged and offers no control. */}
            {isPartLine && updateChargeable ? (
              <label
                className="flex cursor-pointer select-none items-center gap-2"
                title="Unchecked: this line is not billed and does not appear on the customer's document. Its cost still counts toward the job — use it for parts paid for through the service that consumes them."
              >
                {/* The SAME answer the badge, the amount and the price
                        controls give (`chargesRevenue`), never the raw catalog
                        flag: a part that arrived on the document as included in
                        its service charges nothing, and a box reading "ticked"
                        over a row reading "Not billed" tells the operator the
                        line does something it does not do. */}
                <Checkbox
                  checked={billed}
                  onCheckedChange={(checked) => updateChargeable(index, checked === true)}
                />
                <span className="text-muted-foreground">Chargeable</span>
              </label>
            ) : null}
            {showServiceCallCredit ? (
              <label
                className="flex cursor-pointer select-none items-center gap-2"
                title="Checked: this line is part of the visit price and is credited toward the work when the customer proceeds (on the estimate or the resulting job, decided on the fly). Unchecked: always charged — e.g. materials consumed during the visit."
              >
                <Checkbox
                  checked={item.isServiceCallFee !== false}
                  onCheckedChange={(checked) => updateServiceCallCredit?.(index, checked === true)}
                />
                <span className="text-muted-foreground">Credit toward work</span>
              </label>
            ) : null}
            {!isMembershipLine && offered && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={item.isSelected ?? false}
                  onCheckedChange={() => toggleSelected(index)}
                />
                <span className="text-muted-foreground">Selected</span>
              </label>
            )}
            {enableLineAttachments ? (
              <LineItemAttachmentPanel
                estimateId={estimateId}
                lineItemId={item._rowId}
                onPendingFilesChange={onPendingFilesChange}
              />
            ) : null}
          </div>
          {item.pricebookTaxable === false ? (
            <p
              id={`pricebook-taxability-${item._rowId}`}
              className="text-[11px] leading-tight text-muted-foreground"
            >
              {item.taxable
                ? 'Pricebook default is not taxable; overridden for this estimate.'
                : 'Not taxable in pricebook'}
            </p>
          ) : null}
        </div>

        <div className="shrink-0 text-right">
          <p className="text-xs font-semibold text-foreground">Line subtotal</p>
          <p className="text-sm font-semibold">{lineAmountText}</p>
        </div>
      </div>
    </>
  );

  /*
   * `bodyOnly` renders the DETAILS EDITOR without the card or the header row.
   *
   * The job-create sheet draws its scope as a table — columns with a header,
   * rules instead of boxes — but the editor a line opens into is this one, and
   * it is four hundred lines of price strategy, dynamic pricing, configured
   * parts, equipment, taxability and attachments that already exist and are
   * already tested. Re-authoring it beside a different set of columns would
   * mean maintaining two of it, and the second one would be the one missing a
   * control. So the frame is the caller's and the editor stays here.
   */
  if (bodyOnly) {
    return (
      <div className="space-y-4">
        {identityFields}
        {detailsBody}
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border border-border/60 bg-card ${
        // #5: an unselected optional line is marked with a dashed left accent +
        // an "Optional" badge (below) — NOT a full-card opacity-50, which made
        // the whole editable row (inputs included) look disabled/broken.
        dimmed ? 'border-l-2 border-l-dashed border-l-muted-foreground/40' : ''
      }`}
    >
      {/* Header row */}
      <div
        className="flex cursor-pointer items-center gap-2 px-3 py-2.5"
        // Toggle only on interactions that genuinely STARTED on this header.
        // Closing a portaled Radix Select re-fires a click on whatever sits
        // under the menu, so picking an option in e.g. the type dropdown used
        // to collapse the row — that phantom click has no preceding
        // pointerdown on the header (it happened inside the portal), so the
        // flag stays unset and we ignore it. Synthetic clicks (keyboard,
        // tests' fireEvent.click) carry detail === 0 and are honored.
        onPointerDown={(e) => {
          headerPressRef.current = !(e.target as HTMLElement).closest(
            'button, input, select, textarea, a, [role="combobox"]',
          );
        }}
        onClick={(e) => {
          const startedHere = headerPressRef.current;
          headerPressRef.current = false;
          const onControl = (e.target as HTMLElement).closest(
            'button, input, select, textarea, a, [role="combobox"]',
          );
          if (onControl) return;
          if (startedHere || e.detail === 0) onToggle();
        }}
      >
        <button
          className="shrink-0 cursor-grab touch-none text-muted-foreground/40"
          aria-label="Drag to reorder"
          {...dragHandleProps}
        >
          <GripVertical size={14} />
        </button>

        <div className="min-w-0 flex-1">
          <div
            className={`flex items-center gap-1.5 ${isCustomLine ? 'flex-nowrap' : 'flex-wrap'}`}
          >
            {isCustomLine ? (
              <>
                <Input
                  value={item.name}
                  onChange={(event) => updateName(index, event.target.value)}
                  placeholder="Custom item name"
                  className="h-8 min-w-44 flex-1 bg-background text-sm font-medium"
                />
                {/* Under a section the type is already decided by where the
                    line was added, and re-picking it here only files the line
                    under the wrong heading. */}
                {!sectioned ? (
                  <Select
                    value={normalizeLineItemType(item.type) || 'SERVICE'}
                    onValueChange={(type) => updateType(index, type)}
                  >
                    <SelectTrigger className="h-8 w-32 bg-background text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SERVICE">Service</SelectItem>
                      <SelectItem value="MATERIAL">Material</SelectItem>
                      <SelectItem value="EQUIPMENT">Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                ) : null}
              </>
            ) : (
              <p className="text-sm font-medium leading-snug">{item.name}</p>
            )}
            {item.isUpsell ? (
              <Badge
                variant="outline"
                className="border-warning/30 bg-warning/10 text-[10px] text-warning"
              >
                Upsell
              </Badge>
            ) : null}
            {/* Named rather than just marked: several services can be on one
                document, and "part of a service" does not say which. Custom
                lines render no meta row, so the badge is where this can be said
                for every part. */}
            {sectioned && item.parentLineKey ? (
              <Badge
                variant="outline"
                className="border-primary/30 bg-primary/[0.06] text-[10px] font-normal text-primary"
              >
                Part of {parentService?.name || 'a service'}
              </Badge>
            ) : null}
            {/* Says the whole thing: no price is charged for this line, and
                its cost still counts. Offered on any part the row can draw,
                not only where the price is editable — the price control is
                hidden on an unbilled line, so this badge is what explains it. */}
            {isPartLine && !billed ? (
              <Badge
                variant="outline"
                className="border-muted-foreground/30 bg-muted/40 text-[10px] font-normal text-muted-foreground"
                title="This part is not billed on its own line. Its price is still real and its cost still counts toward the job."
              >
                Not billed
              </Badge>
            ) : null}
            {/* #5: clear "Optional" marker instead of dimming the whole card. */}
            {offered ? (
              <Badge
                variant="outline"
                className="border-muted-foreground/30 bg-muted/40 text-[10px] text-muted-foreground"
              >
                {item.isSelected ? 'Optional · included' : 'Optional'}
              </Badge>
            ) : null}
            {item.provenance ? (
              <Badge
                variant="outline"
                className={
                  item.provenance.source === 'ESTIMATE'
                    ? 'border-blue-200 bg-blue-50 text-[10px] font-medium text-blue-700'
                    : 'border-violet-200 bg-violet-50 text-[10px] font-medium text-violet-700'
                }
              >
                {item.provenance.label}
              </Badge>
            ) : null}
            {!isCustomLine ? (
              <TMStatusBadge
                label={tmModeLabel}
                warning={tmIncompleteReason}
                laborRate={item.laborRate}
              />
            ) : null}
          </div>
          {/* WHAT THIS LINE COMES TO. Drawn for a hand-typed line as well as a
              catalog one: it carries a real price, a real cost and real tax, and
              a row that shows none of them is the one row on the estimate whose
              money the operator has to open to see.

              The TYPE is the exception — a custom row already states it, in the
              selector beside the name or in the section it was filed under. */}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {!isCustomLine ? (
              <>
                <span className="capitalize">{item.type.toLowerCase()}</span>
                <span>·</span>
              </>
            ) : null}
            <span>
              {isPartLine ? 'Price ' : ''}
              {preview && effectiveSubtotal < preview.lineSubtotal && billed ? (
                <>
                  <span
                    className="line-through"
                    aria-label={`List price ${formatCurrency(preview.lineSubtotal)}`}
                  >
                    {formatCurrency(preview.lineSubtotal)}
                  </span>{' '}
                  <span
                    className="font-medium text-foreground"
                    aria-label={`Final price ${formatCurrency(effectiveSubtotal)}`}
                  >
                    {lineAmountText}
                  </span>
                </>
              ) : isPartLine && !billed ? (
                formatCurrency(0)
              ) : (
                lineAmountText
              )}
            </span>
            {lineCostText ? (
              <>
                <span>·</span>
                <span title="What this part costs us; cost affects margin whether the part is included or billed separately.">
                  Cost {lineCostText}
                </span>
              </>
            ) : null}
            {effectiveTax > 0 && (
              <span className="text-muted-foreground/60">+{formatCurrency(effectiveTax)} tax</span>
            )}
            {/* NOT-590 — a service's parts are their own priced lines now,
                    so the rep sees them in the list rather than behind a
                    disclosure. This marks which service a part belongs to.
                    Sectioned rows say it in the badge above, which also names
                    the service and shows on a custom line. */}
            {!sectioned && item.parentLineKey ? (
              <span className="text-muted-foreground/60">· part of a service</span>
            ) : null}
            {membershipDiscount ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 font-medium text-success">
                <span>✦</span>
                {/* NOT-605 — a per-item member price wins over the plan
                        discount, so label it as such instead of the plan's %. */}
                {preview &&
                String(item.type ?? '')
                  .trim()
                  .toUpperCase() === 'FEE' &&
                (preview.membershipDiscountAmount ?? 0) >= preview.lineSubtotal
                  ? 'Waived'
                  : lineIsWaivedByMembership(item, membershipDiscount)
                    ? 'Waived'
                    : item.memberPrice != null && item.memberPrice < item.basePrice
                      ? 'Member price'
                      : membershipDiscount.discountType === 'PERCENT'
                        ? `${membershipDiscount.discountValue}% off`
                        : 'Member discount'}
              </span>
            ) : null}
          </div>
          {item.membershipPlanSummary ? (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {item.membershipPlanSummary.short}
              {item.membershipCustomerChoosesBillingOption ? ' · Customer chooses billing' : ''}
              {item.membershipPlanSummary.complimentary ? ' · Free option, office only' : ''}
            </p>
          ) : null}
        </div>

        {/* Price. Compact and inline, edited exactly like the quantity beside
            it, because the price is the number the operator changes most.
            NOT drawn on a line that charges nothing: there is no price to set
            where nobody is billed, and a field standing over a "Not billed"
            badge and a dashed amount only invites the operator to type a number
            that changes no total. The stored price is untouched — ticking
            Chargeable brings both it and this control straight back. */}
        {showRowPrice && billed ? (
          <div className="flex shrink-0 items-center gap-0.5">
            <div className="relative">
              <span
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
                aria-hidden="true"
              >
                $
              </span>
              <Input
                type="number"
                min={0}
                step={0.01}
                aria-label={`${item.name || 'Line'} unit price`}
                title={
                  rowPriceOverridden
                    ? `Priced at ${formatCurrency(unitPriceOf(item))} on this estimate. Catalog price is ${formatCurrency(
                        basePriceOf(item),
                      )}. Clear the field to go back to it.`
                    : 'Price for this estimate. Clear the field to go back to the catalog price.'
                }
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onBlur={commitRowPrice}
                disabled={isMembershipLine}
                aria-description={
                  isMembershipLine
                    ? 'Price is set by the selected membership billing option.'
                    : undefined
                }
                className={cn(
                  'h-8 w-24 pl-5 pr-1.5 text-right text-xs',
                  INPUT_CLS,
                  rowPriceOverridden && 'border-primary/60 font-medium text-primary',
                )}
              />
            </div>
            {rowPriceOverridden && !isMembershipLine ? (
              <button
                type="button"
                aria-label={`Revert ${item.name || 'this line'} to the catalog price`}
                title={`Revert to the catalog price of ${formatCurrency(basePriceOf(item))}`}
                onClick={revertRowPrice}
                className="shrink-0 text-primary/70 hover:text-primary"
              >
                <RotateCcw size={13} />
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Qty */}
        <div className="flex shrink-0 items-center gap-1">
          <Input
            type="number"
            min={item.parentLineKey ? 0 : 1}
            value={isMembershipLine ? String(item.quantity) : qtyInput}
            onChange={(e) => setQtyInput(e.target.value)}
            disabled={isMembershipLine}
            aria-description={
              isMembershipLine
                ? hasLegacyMembershipQuantity
                  ? `This legacy document stores quantity ${item.quantity}. Remove and re-add the membership to repair it to quantity 1.`
                  : 'A membership plan can only be added once per estimate option.'
                : undefined
            }
            onBlur={() => {
              if (isMembershipLine) return;
              const v = parseInt(qtyInput, 10);
              const minimum = item.parentLineKey ? 0 : 1;
              const committed = isNaN(v) || v < minimum ? minimum : v;
              updateQuantity(index, committed);
              setQtyInput(String(committed));
            }}
            className={`h-8 w-16 px-2 text-center text-xs tabular-nums sm:w-20 ${INPUT_CLS}`}
          />
          {hasLegacyMembershipQuantity ? (
            <span className="max-w-28 text-[10px] leading-tight text-amber-700 dark:text-amber-400">
              Legacy qty {item.quantity}; re-add to repair
            </span>
          ) : null}
          {/* The SELECTOR keeps the canonical code: someone choosing a unit is
              choosing the value that gets stored, and "Hour (HR)" tells them
              which. The read-only suffix beside the number is not that — it is
              the unit being READ, so it reads the way it does everywhere else,
              and says nothing at all where there is nothing to say. */}
          {updateUnit && !isMembershipLine ? (
            <UnitSelect value={item.unit ?? ''} onChange={(unit) => updateUnit(index, unit)} />
          ) : !isMembershipLine && unitLabel(item.unit, item.quantity, 'word') ? (
            <span
              className="max-w-[3.5rem] truncate text-xs text-muted-foreground"
              title={item.unit ?? undefined}
            >
              {unitLabel(item.unit, item.quantity, 'word')}
            </span>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 text-muted-foreground/40 hover:text-muted-foreground"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {isMembershipLine && onConfigureMembership ? (
          <button
            type="button"
            onClick={() => onConfigureMembership(index)}
            className="shrink-0 text-muted-foreground/50 hover:text-foreground"
            aria-label={`Configure ${item.name || 'membership'}`}
            title="Configure membership"
          >
            <Settings2 size={14} />
          </button>
        ) : null}

        {/* Only zero-priced equipment has a visibility choice. Any priced part
            must print to explain its amount, while zero-priced materials are
            internal consumable scope and never print. */}
        {normalizedType === 'EQUIPMENT' && effectiveSubtotal === 0 && updateHiddenFromCustomer ? (
          <button
            type="button"
            aria-pressed={item.hiddenFromCustomer !== true}
            aria-label={
              item.hiddenFromCustomer === true
                ? `Show ${item.name || 'this line'} on the customer's estimate`
                : `Hide ${item.name || 'this line'} from the customer's estimate`
            }
            title={
              item.hiddenFromCustomer === true
                ? 'Hidden from the customer estimate. Its cost and its contribution to the service are unchanged.'
                : 'Shown on the customer estimate. Hiding it changes nothing but the customer view — the cost and the contribution to the service stay.'
            }
            onClick={() => updateHiddenFromCustomer(index, item.hiddenFromCustomer !== true)}
            className={`shrink-0 ${
              item.hiddenFromCustomer === true
                ? 'text-muted-foreground/40 hover:text-muted-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {item.hiddenFromCustomer === true ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => removeLineItem(index)}
          className="shrink-0 text-muted-foreground/40 hover:text-destructive"
          aria-label="Remove item"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="space-y-4 border-t border-border/60 bg-muted/25 px-4 py-4">
          {detailsBody}
        </div>
      )}
    </div>
  );
}

// ─── Sortable mobile card ─────────────────────────────────────────────────────

function SortableMobileCard(props: Parameters<typeof MobileLineItemCard>[0]) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.item._rowId,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <MobileLineItemCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

// ─── Sortable Row ─────────────────────────────────────────────────────────────

// ─── Section Props ────────────────────────────────────────────────────────────

interface EstimateLineItemsSectionProps {
  open: boolean;
  onToggle: () => void;
  bare?: boolean;
  picker: ReactNode;
  /**
   * Opt into the split editor: services, equipment and materials each get their
   * own heading and their own search, scoped to that section's types.
   *
   * Given a section, return the picker for it. Callers that pass this can leave
   * `picker` as the fallback for anything the sections do not cover.
   */
  sectionedPicker?: (section: LineSection) => ReactNode;
  lineItems: EstimateLineItem[];
  lineItemPreviews: LineItemPreview[];
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  estimateId?: string;
  updateQuantity: (index: number, qty: number) => void;
  updateName?: (index: number, name: string) => void;
  updateType?: (index: number, type: string) => void;
  updateUnit?: (index: number, unit: string) => void;
  updateBasePrice: (index: number, price: number) => void;
  /**
   * Price a MATERIAL / EQUIPMENT line for THIS document (`manualPriceOverride`,
   * never `basePrice`). `null` removes the override and the catalog price
   * applies again. Omit where the surface cannot save it.
   */
  updateManualPriceOverride?: (index: number, price: number | null) => void;
  updatePriceStrategy: (index: number, strategy: 'FLAT' | 'DYNAMIC') => void;
  updateLaborRate: (index: number, rate: number) => void;
  updateEstimatedHours: (index: number, estimatedHours: number) => void;
  /** The labor rows on a FLAT service — cost only, never a price. */
  updateLaborCostLines?: (
    index: number,
    lines: ServiceLaborLine[],
    hours: number,
    cost: number,
  ) => void;
  /** Configured labor rates the flat-service labor block fills its cost from. */
  laborRateProfiles?: LaborRateProfile[];
  updateDynamicPricingConfig?: (index: number, config: DynamicServicePricingConfig) => void;
  updateDynamicMarkupProfile?: (
    index: number,
    target: 'MATERIAL' | 'EQUIPMENT',
    profile: MarkupProfile,
  ) => void;
  updateDescription?: (index: number, description: string) => void;
  updateMarkupPercent?: (index: number, pct: number) => void;
  updateCostBasis?: (index: number, cost: number) => void;
  /** Job-only: active suppliers offered as the source of a custom part cost. */
  costSuppliers?: Supplier[];
  updateCostSource?: (index: number, supplier: Pick<Supplier, 'id' | 'name'> | null) => void;
  updateTaxable?: (index: number, taxable: boolean) => void;
  /**
   * A custom line's tax class and GL accounts. Omitted where the surface
   * cannot save them, which hides the block rather than showing controls
   * whose values would be dropped on save.
   */
  updateTaxAccounting?: (index: number, patch: LineTaxAccounting) => void;
  /** A custom line's member pricing. Omitted where it cannot be saved. */
  updateMemberPricing?: (index: number, patch: LineMemberPricing) => void;
  /**
   * Keep a hand-typed line as a catalog item. Omitted where the surface cannot
   * relink the row afterwards, which hides the action rather than creating an
   * item the document would go on ignoring.
   */
  onSaveLineToPricebook?: (
    rowId: string,
    item: { id: string; code: string; itemVersion?: number },
  ) => void;
  /** Per-line override of the catalog's chargeable flag (MATERIAL / EQUIPMENT). */
  updateChargeable?: (index: number, chargeable: boolean) => void;
  /** EQUIPMENT — keep this line off the customer's document (customer view only). */
  updateHiddenFromCustomer?: (index: number, hidden: boolean) => void;
  showServiceCallCredit?: boolean;
  updateServiceCallCredit?: (index: number, credit: boolean) => void;
  toggleOptional: (index: number) => void;
  toggleSelected?: (index: number) => void;
  removeLineItem: (index: number) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  membershipEligibleRowIds: Set<string>;
  membershipInfo: MembershipDiscountInfo | null;
  onPendingFilesChange?: (rowId: string, files: File[]) => void;
  enableLineAttachments?: boolean;
  /** @deprecated The empty state no longer names the entity. */
  emptyStateEntityLabel?: string;
  customerId?: string | null;
  serviceAddressId?: string | null;
  /**
   * The search that adds a material or equipment to a SERVICE — drawn inside the
   * dynamic configuration wizard's Parts step, where changing what the service
   * contains is what the operator is there to do. Given the service and its
   * index, return the picker for it.
   */
  componentPicker?: (parent: EstimateLineItem, parentIndex: number) => ReactNode;
  /**
   * Take a part back out of a service's configuration, by its position in that
   * configuration. Omit where the surface cannot save it; the control is hidden
   * then rather than appearing to work.
   */
  removeConfiguredPart?: (index: number, partIndex: number) => void;
  updateEquipmentAssociation?: (
    index: number,
    equipmentIds: string[],
    locationOverrideReason?: string,
  ) => void;
  /** Job-only per-unit installation facts, persisted through the equipment API. */
  equipmentInstallationUnits?: Record<string, JobEquipmentInstallationUnitDraft[]>;
  updateEquipmentInstallationUnits?: (
    lineId: string,
    units: JobEquipmentInstallationUnitDraft[],
  ) => void;
  updateMembershipEquipmentAssociation?: (
    index: number,
    equipmentIds: string[],
    locationOverrideReason: string | undefined,
    selectedEquipment: Equipment[],
  ) => void;
  /** Reopen the membership sale configurator for this existing row. */
  onConfigureMembership?: (index: number) => void;
  membershipEquipmentPricingStatus?: 'ready' | 'loading' | 'error';
  membershipEquipmentPricingStatusByRowId?: ReadonlyMap<string, 'ready' | 'loading' | 'error'>;
  /** Bundle display control. Omit to render bundles read-only. */
  updateComponentDisplayMode?: (index: number, mode: 'ITEMIZED' | 'ROLLED_UP') => void;
  // legacy props kept for backward compat
  visibleAddresses?: unknown;
  showAll?: unknown;
  maxVisibleAddresses?: unknown;
  onToggleShowAll?: unknown;
}

// ─── Section ─────────────────────────────────────────────────────────────────

const noop = () => {};

export function EstimateLineItemsSection({
  open,
  onToggle,
  bare = false,
  picker,
  sectionedPicker,
  lineItems,
  lineItemPreviews,
  pricingMode,
  estimateId,
  updateQuantity,
  updateName = noop,
  updateType = noop,
  updateUnit,
  updateBasePrice,
  updateManualPriceOverride,
  updatePriceStrategy,
  updateLaborRate,
  updateEstimatedHours,
  updateLaborCostLines,
  laborRateProfiles,
  updateDynamicPricingConfig,
  updateDynamicMarkupProfile,
  updateDescription = noop,
  updateMarkupPercent = noop,
  updateCostBasis = noop,
  costSuppliers,
  updateCostSource,
  updateTaxable = noop,
  updateTaxAccounting,
  updateMemberPricing,
  onSaveLineToPricebook,
  updateChargeable,
  updateHiddenFromCustomer,
  showServiceCallCredit = false,
  updateServiceCallCredit = noop,
  toggleOptional,
  toggleSelected = noop,
  removeLineItem,
  onReorder,
  membershipEligibleRowIds,
  membershipInfo,
  onPendingFilesChange,
  enableLineAttachments = true,
  customerId,
  serviceAddressId,
  updateEquipmentAssociation,
  equipmentInstallationUnits,
  updateEquipmentInstallationUnits,
  updateMembershipEquipmentAssociation,
  onConfigureMembership,
  membershipEquipmentPricingStatus = 'ready',
  membershipEquipmentPricingStatusByRowId,
  updateComponentDisplayMode,
  componentPicker,
  removeConfiguredPart,
}: EstimateLineItemsSectionProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // NOT-220: single-open accordion for the line-item editors. Every row starts
  // CLOSED — including one just added — and the row header toggles.
  //
  // A new line used to open itself, which reads well for one line and badly for
  // the way estimates are actually built: adding several in a row made each
  // arrival collapse the one before it and push the list down, so the common
  // case (add five items, price them after) fought the editor. Nothing about a
  // fresh line needs attention that its own row does not already show, and the
  // header is one click away when it does.
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const toggleRow = useCallback(
    (rowId: string) => setExpandedRowId((cur) => (cur === rowId ? null : rowId)),
    [],
  );

  const isSectioned = Boolean(sectionedPicker);

  function handleDragEnd(event: {
    active: { id: string | number };
    over: { id: string | number } | null;
  }) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = lineItems.findIndex((item) => item._rowId === active.id);
    const newIndex = lineItems.findIndex((item) => item._rowId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    // The drag happened between RENDERED rows; `onReorder` moves the raw array.
    // Translating between the two is what keeps a grouped row from moving a
    // different line than the one dragged.
    //
    // Sectioned, every line is drawn on its own — no card carries its parts
    // along — so the two coordinate spaces coincide and the drag means exactly
    // what it looks like. Anchoring a part to its service here would instead
    // move the SERVICE when the operator dragged a material within Materials.
    const move = isSectioned
      ? { from: oldIndex, to: newIndex }
      : groupedReorder(lineItems, oldIndex, newIndex);
    if (!move) return;
    onReorder(move.from, move.to);
  }

  // The lines in the sale, with a declined service taking its parts with it —
  // the same set the totals are built from, so a part whose service was
  // declined is drawn dimmed beside a subtotal that no longer includes it.
  const soldRows = soldLineSet(lineItems);
  const rowProps = (item: EstimateLineItem, index: number) => ({
    item,
    lineItems,
    preview: lineItemPreviews[index],
    outOfSale: !soldRows.has(item),
    pricingMode,
    index,
    estimateId,
    updateQuantity,
    updateName,
    updateType,
    updateUnit,
    updateBasePrice,
    updateManualPriceOverride,
    updatePriceStrategy,
    updateLaborRate,
    updateEstimatedHours,
    updateLaborCostLines,
    laborRateProfiles,
    updateDynamicPricingConfig,
    updateDynamicMarkupProfile,
    updateDescription,
    updateMarkupPercent,
    updateCostBasis,
    costSuppliers,
    updateCostSource,
    updateTaxable,
    updateTaxAccounting,
    updateMemberPricing,
    onSaveLineToPricebook,
    updateChargeable,
    updateHiddenFromCustomer,
    toggleOptional,
    toggleSelected,
    showServiceCallCredit,
    updateServiceCallCredit,
    removeLineItem,
    membershipDiscount:
      membershipEligibleRowIds.has(item._rowId) && membershipInfo ? membershipInfo : null,
    onPendingFilesChange,
    enableLineAttachments,
    customerId,
    serviceAddressId,
    updateEquipmentAssociation,
    equipmentInstallationUnits: equipmentInstallationUnits?.[item._rowId],
    updateEquipmentInstallationUnits: updateEquipmentInstallationUnits
      ? (units: JobEquipmentInstallationUnitDraft[]) =>
          updateEquipmentInstallationUnits(item._rowId, units)
      : undefined,
    updateMembershipEquipmentAssociation,
    onConfigureMembership,
    membershipEquipmentPricingStatus:
      membershipEquipmentPricingStatusByRowId?.get(item._rowId) ?? membershipEquipmentPricingStatus,
    componentPicker,
    removeConfiguredPart,
    sectioned: isSectioned,
  });

  const drawRow = (item: EstimateLineItem, index: number) =>
    item.type === 'MEMBERSHIP_SETUP' ? (
      <div
        key={item._rowId}
        className="flex items-center justify-between gap-4 px-4 py-2 text-sm text-muted-foreground"
      >
        <span>
          {item.name}
          <span className="ml-2 text-xs">One time</span>
        </span>
        <span className="tabular-nums">{formatCurrency(item.basePrice)}</span>
      </div>
    ) : (
      <SortableMobileCard
        key={item._rowId}
        dragHandleProps={{}}
        {...rowProps(item, index)}
        expanded={expandedRowId === item._rowId}
        onToggle={() => toggleRow(item._rowId)}
      />
    );

  /**
   * The lines a section draws: every line of that KIND, a service's parts
   * included.
   *
   * The parent relationship is untouched — it is still what makes a part's
   * markup feed its service's price — but the operator reads the document a
   * kind at a time, so a service's materials belong under Materials and its
   * equipment under Equipment. Each row names its service (see the card's
   * badge), which is what the service's card used to say by containing them.
   *
   * The index is taken BEFORE filtering. Every row callback addresses the raw
   * array positionally, so a section is a view of that array and never a
   * renumbering of it.
   */
  const sectionLines = (section: LineSection['id']) =>
    lineItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => estimateSectionForType(item.type) === section);

  // The flat list draws every group, with a service's parts nested inside its
  // card. `lineItems` is the full array, so the indices the row callbacks
  // receive stay absolute.
  const drawLines = () =>
    renderGroupedLines<EstimateLineItem>({
      lineItems,
      renderRow: drawRow,
      formatCurrency,
      // Bundle economics run on the PRICED subtotal, not the catalog
      // price, so a discounted part shrinks the materials share the
      // same way it shrinks the bill. The LIST subtotal, before membership
      // and document discounts — the figure the totals panel's Subtotal row,
      // the detail page and the invoice all state for a bundle, so the header
      // says the same number on every surface; the discounts are rows of
      // their own beneath it.
      // The fallback resolves the price the same way (`unitPriceOf`) rather
      // than reading `basePrice`, which is the catalog's. Now that this editor
      // records a typed price as an override, `basePrice` is the ONE number on
      // the line that is not what it sells for.
      amountOf: (item) =>
        lineItemPreviews[lineItems.indexOf(item)]?.lineSubtotal ??
        unitPriceOf(item) * item.quantity,
      onDisplayModeChange: updateComponentDisplayMode,
      componentsInlineFor: (parent) => Boolean(parent.dynamicPricingConfig?.enabled),
    });

  // One drag context wraps every section, so a line can still be dragged from
  // one section to another; `handleDragEnd` resolves both ends against the raw
  // array and the drop re-sections the line by its type.
  const dnd = (children: ReactNode) => (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {/* The RENDERED order, not the array order. Grouping draws a
          service's parts inside its card, and sections draw the lines a kind at
          a time, so either way the two differ from the array — and dnd-kit maps
          a drag by index, so feeding it the array order moves a different row
          than the one the operator dragged. */}
      <SortableContext
        items={(isSectioned
          ? LINE_SECTIONS.flatMap((section) => sectionLines(section.id).map(({ item }) => item))
          : groupedLineOrder(lineItems)
        ).map((item) => item._rowId)}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );

  if (sectionedPicker) {
    const sectioned = (
      <div className="space-y-4">
        {LINE_SECTIONS.map((section) => {
          // A part counts under its own kind like any other line — counting only
          // top-level lines would print "No materials yet." over a section that
          // is drawing a service's materials.
          const lines = sectionLines(section.id);

          return (
            <section key={section.id} className="space-y-2 rounded-lg border bg-muted/10 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{section.title}</p>
                {lines.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {lines.length} {lines.length === 1 ? 'line' : 'lines'}
                  </span>
                )}
              </div>

              {sectionedPicker(section)}

              {lines.length > 0 ? (
                <div className="space-y-2">
                  {lines.map(({ item, index }) => drawRow(item, index))}
                </div>
              ) : (
                <p className="px-1 py-2 text-xs text-muted-foreground">
                  No {section.title.toLowerCase()} yet.
                </p>
              )}
            </section>
          );
        })}
      </div>
    );

    const sectionedBody = lineItems.length > 0 ? dnd(sectioned) : sectioned;
    if (bare) return <div className="space-y-4">{sectionedBody}</div>;
    return (
      <FormAccordionCard
        open={open}
        header={
          <FormSectionHeader
            icon={ListOrdered}
            title="Line items"
            open={open}
            onToggle={onToggle}
          />
        }
        bodyClassName="space-y-4"
      >
        {sectionedBody}
      </FormAccordionCard>
    );
  }

  const body = (
    <>
      {/* The picker is a control, not a banner. Left to fill the sheet it drew
          a 981px search field with "Custom item" stranded 260px away at the
          far edge; capped, the two read as one toolbar and the lines below get
          the width. */}
      <div className="max-w-2xl">{picker}</div>
      {lineItems.length > 0 ? (
        /* A measure, not the whole sheet. Each row puts its name at the left
           and its quantity, unit and controls at the right, so across 1,104px
           the two ends of one line sat 900px apart with nothing between them.
           Capped, a line reads as a line — and the space it gives up on the
           right is the same space the field grids above it leave. */
        dnd(<div className="max-w-4xl space-y-3">{drawLines()}</div>)
      ) : (
        /* One quiet line. A dashed panel with a 32px magnifier telling you to
           "search above" sat directly under a search box already labelled
           "Search pricebook items…" — 125px of chrome to repeat the field
           above it. Nothing is missing here; there is simply nothing yet. */
        <p className="py-1 text-[13px] text-foreground/70">
          No items yet. Search above, or add a custom one.
        </p>
      )}
    </>
  );

  if (bare) {
    return <div className="space-y-5">{body}</div>;
  }

  return (
    <FormAccordionCard
      open={open}
      header={
        <FormSectionHeader icon={ListOrdered} title="Line items" open={open} onToggle={onToggle} />
      }
      bodyClassName="space-y-4"
    >
      {body}
    </FormAccordionCard>
  );
}
